import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Container } from "@/components/ui-kit";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import ScopedTaxWorkspace from "@/components/ScopedTaxWorkspace";
import AdminNavTabs from "@/components/AdminNavTabs";
import useDialogFocus from "@/hooks/useDialogFocus";
import {
  fetchBillingOverview, fetchBillingDiagnostics, fetchBillingCustomers, fetchBillingPayments,
  fetchBillingNotifications, retryBillingNotification,
  fetchBillingReconciliation, fetchTaxProfile, patchTaxProfile, fetchTaxElections, upsertTaxElection,
  fetchTaxEstimate, fetchTaxFilings, createTaxFiling, changeTaxFilingStatus, fetchBillingReport,
  fetchComplianceReminders, fetchSetupChecklist,
  fetchIncomeClassifications, upsertIncomeClassification, fetchTaxAutomationHealth, fetchAdminTaxCalendar,
  fetchMonthlyCloseSummary, closeMonth, reopenMonth, updateMonthlyCloseChecklist, fetchTaxBusinesses,
  fetchAccounts, createAccount, updateAccount, archiveAccount, activateAccount, deleteAccount,
  fetchBillingLedger, fetchBillingTrialBalance, fetchBillingExpenses, createBillingExpense,
  fetchGeneralJournal, fetchCashReceipts, fetchCashDisbursements, fetchLedgerSource,
  createAdjustment, reverseJournal, downloadReportPayload,
  fetchComplianceDocuments, uploadComplianceDocument, downloadComplianceDocument, viewComplianceDocument,
  editComplianceDocument, replaceComplianceDocument, archiveComplianceDocument, deleteComplianceDocument,
  linkComplianceDocument, unlinkComplianceDocument, fetchFinanceCapabilities, fetchLedgerStatement, patchExpense,
  previewPaymongoSampleEmail, sendPaymongoSampleEmail,
} from "@/lib/api";
import { FinanceCapsContext, useFinanceCan } from "@/components/FinanceCapabilities";
import { ReportsCatalogSection, TransactionsSection, PackagesSection, BooksComplianceWorkspace } from "@/components/FinanceReports";
import { adsenseDiagnostics } from "@/lib/adsConfig";

function money(minor, currency = "PHP") {
  const symbol = currency === "PHP" ? "₱" : currency === "EUR" ? "€" : `${currency} `;
  return `${symbol}${((minor || 0) / 100).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const TABS = ["Overview", "Chart of Accounts", "Books & Reports", "Document Center", "Customers", "Payments", "Reconciliation", "BIR & Tax", "Diagnostics"];

function StatCard({ label, value, tone }) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-[#94A3B8]">{label}</p>
      <p className={`mt-1.5 text-xl font-bold font-display ${tone || "text-[#0F172A]"}`}>{value}</p>
    </div>
  );
}

const SEVERITY_STYLE = {
  overdue: "border-red-300 bg-red-50 text-red-800",
  due_today: "border-amber-300 bg-amber-50 text-amber-800",
  upcoming: "border-[#E2E8F0] bg-white text-[#0F172A]",
};

// Meaningful status labels instead of a bare colored dot — nobody should have to
// infer what a color means (actionable-checklist task, item 10).
const STATUS_LABEL_STYLE = {
  "Complete": "bg-emerald-100 text-emerald-800",
  "Action required": "bg-amber-100 text-amber-800",
  "Overdue": "bg-red-100 text-red-800",
  "Needs verification": "bg-slate-200 text-slate-700",
  "Waiting on external provider": "bg-sky-100 text-sky-800",
  "Internal recommendation": "bg-violet-100 text-violet-800",
  "Blocked": "bg-red-100 text-red-800",
};

function StatusChip({ label }) {
  return <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_LABEL_STYLE[label] || "bg-slate-200 text-slate-700"}`}>{label}</span>;
}

// Category badge ("BIR / Tax", "Accounting", "PayMongo", "Security", "Internal") and
// deadline-type badge ("Legal deadline" vs "Internal target" vs ...) — item 11, so an
// internal housekeeping item never LOOKS like a government compliance violation.
const DEADLINE_TYPE_LABEL = {
  legal_deadline: "Legal deadline", payment_provider: "PayMongo requirement",
  internal_target: "Internal target", recommended: "Recommended", external_setup: "External setup",
};
const DEADLINE_TYPE_STYLE = {
  legal_deadline: "bg-red-50 text-red-700 border-red-200", payment_provider: "bg-sky-50 text-sky-700 border-sky-200",
  internal_target: "bg-violet-50 text-violet-700 border-violet-200", recommended: "bg-slate-50 text-slate-600 border-slate-200",
  external_setup: "bg-amber-50 text-amber-700 border-amber-200",
};

function Badge({ children, className }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${className}`}>{children}</span>;
}

function ItemBadges({ category, deadlineType }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {category && <Badge className="border-[#E2E8F0] bg-[#F8FAFC] text-[#475569]">{category}</Badge>}
      {deadlineType && <Badge className={DEADLINE_TYPE_STYLE[deadlineType] || "border-slate-200 bg-slate-50 text-slate-600"}>{DEADLINE_TYPE_LABEL[deadlineType] || deadlineType}</Badge>}
    </div>
  );
}

// The clickable-row detail panel every reminder/checklist item opens into — item 1/2.
// `item` is the enriched object with .label/.title, .category, .deadlineType,
// .statusLabel and .detail (what_is_this/why_it_matters/what_to_do/where/when/
// after_completion/need_help/why_am_i_seeing_this from finance_help_content.py).
function DetailDrawer(props) {
  if (!props.item) return null;
  return <DetailDrawerBody {...props} />;
}

function DetailDrawerBody({ item, onClose, onGoTo }) {
  const dialogRef = useDialogFocus(onClose);
  const d = item.detail || {};
  return (
    <div ref={dialogRef} className="fixed inset-0 z-50 flex justify-end bg-black/30" role="dialog" aria-modal="true" aria-label={item.title} onClick={onClose}>
      <div className="h-full w-full max-w-lg overflow-y-auto bg-white shadow-xl focus:outline-none" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 border-b border-[#E2E8F0] bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg font-bold font-display text-[#0F172A]">{item.title}</h2>
            <button onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#0F172A]">✕</button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {item.statusLabel && <StatusChip label={item.statusLabel} />}
            <ItemBadges category={item.category} deadlineType={item.deadlineType} />
          </div>
        </div>
        <div className="space-y-5 p-5 text-sm">
          {d.why_am_i_seeing_this && (
            <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">Why am I seeing this?</p>
              <p className="mt-1 text-[#475569]">{d.why_am_i_seeing_this}</p>
            </div>
          )}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">What is this?</h3>
            <p className="mt-1 text-[#0F172A]">{d.what_is_this}</p>
          </section>
          {d.why_it_matters && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">Why does it matter?</h3>
              <p className="mt-1 text-[#0F172A]">{d.why_it_matters}</p>
            </section>
          )}
          {d.what_to_do?.length > 0 && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">What do I need to do?</h3>
              <ol className="mt-1 list-decimal space-y-1 pl-5 text-[#0F172A]">
                {d.what_to_do.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
            </section>
          )}
          {d.where?.length > 0 && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">Where do I do it?</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {d.where.map((w) => (
                  <button key={w.label} onClick={() => { onGoTo(w.tab, w.focus); onClose(); }}
                    className="rounded-lg bg-[#0F172A] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#1E293B]">{w.label} →</button>
                ))}
              </div>
            </section>
          )}
          {d.when && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">When should it be done?</h3>
              <p className="mt-1 text-[#0F172A]">{d.when}</p>
            </section>
          )}
          {d.after_completion && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">What happens after I complete it?</h3>
              <p className="mt-1 text-[#0F172A]">{d.after_completion}</p>
            </section>
          )}
          {d.need_help && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">Need help?</h3>
              <p className="mt-1 text-[#475569]">{d.need_help}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function ComplianceRemindersCard({ token, onGoTo, businessId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { fetchComplianceReminders(token, businessId).then(setData).catch(() => {}); }, [token, businessId]);
  if (!data) return null;
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm mb-6" data-testid="compliance-reminders-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold font-display text-[#0F172A]">Compliance reminders</h3>
        {data.overdue_count > 0 && <span className="rounded-full bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-1">{data.overdue_count} overdue</span>}
      </div>
      {data.reminders.length === 0 ? (
        <p className="text-sm text-[#475569]" data-testid="compliance-reminders-empty">Nothing due — you're caught up on filings and monthly close.</p>
      ) : (
        <div className="space-y-2">
          {data.reminders.map((r) => (
            <button key={r.key} type="button" onClick={() => setOpen(r)}
              className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition-colors hover:border-[#0F172A] ${SEVERITY_STYLE[r.severity]}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="font-semibold">{r.title}</span>
                <span className="text-xs uppercase tracking-wide font-medium">{r.severity.replace("_", " ")}</span>
              </div>
              <p className="mt-1 text-xs opacity-90">{r.detail_note || r.detail?.why_am_i_seeing_this || ""}</p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <ItemBadges category={r.category} deadlineType={r.deadline_type} />
                <span className="shrink-0 text-xs font-semibold underline">View details →</span>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && (
        <DetailDrawer
          item={{ title: open.title, category: open.category, deadlineType: open.deadline_type, statusLabel: open.severity === "overdue" ? "Overdue" : "Action required", detail: open.detail }}
          onClose={() => setOpen(null)} onGoTo={onGoTo}
        />
      )}
    </div>
  );
}

function SetupChecklistCard({ token, onGoTo, businessId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { fetchSetupChecklist(token, businessId).then(setData).catch(() => {}); }, [token, businessId]);
  if (!data) return null;
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm mb-6" data-testid="setup-checklist-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold font-display text-[#0F172A]">External setup still required</h3>
        <span className="rounded-full bg-[#F1F5F9] text-[#475569] text-xs font-semibold px-2.5 py-1">{data.outstanding_count} outstanding</span>
      </div>
      <div className="space-y-2">
        {data.items.map((item) => (
          <button key={item.key} type="button" onClick={() => setOpen(item)}
            className="flex w-full items-start justify-between gap-3 rounded-lg border border-[#E2E8F0] px-4 py-3 text-left transition-colors hover:border-[#0F172A]">
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#0F172A]">{item.label}</p>
              <p className="text-xs text-[#94A3B8]">{item.note}</p>
              <div className="mt-2"><ItemBadges category={item.category} deadlineType={item.deadline_type} /></div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <StatusChip label={item.status_label} />
              <span className="text-xs font-semibold text-[#0F172A] underline">Fix this →</span>
            </div>
          </button>
        ))}
      </div>
      {open && (
        <DetailDrawer
          item={{ title: open.label, category: open.category, deadlineType: open.deadline_type, statusLabel: open.status_label, detail: open.detail }}
          onClose={() => setOpen(null)} onGoTo={onGoTo}
        />
      )}
    </div>
  );
}

function previousPeriod(today = new Date()) {
  const y = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
  const m = today.getMonth() === 0 ? 12 : today.getMonth();
  return `${y}-${String(m).padStart(2, "0")}`;
}

// Guided Month-End Close workflow (actionable-checklist task, items 3-4). Reuses the
// EXISTING MonthlyClose backend (16 real checklist keys, admin_close_month,
// admin_reopen_month) — this only adds a friendly, grouped, "X of Y completed" view
// on top via GET .../monthly-close/{period}/summary, and writes through the same
// PATCH .../checklist endpoint per real item so nothing new is invented server-side
// beyond that one read-only summary + the live unreconciled-payments close guard.
function MonthEndCloseCard({ token, onGoTo, businessId, businessName }) {
  const can = useFinanceCan();
  const [period, setPeriod] = useState(previousPeriod());
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [closeNotes, setCloseNotes] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => { fetchMonthlyCloseSummary(token, period, businessId).then(setSummary).catch(() => {}); };
  useEffect(load, [token, period, businessId]);

  const toggleItem = async (itemKey, done) => {
    await updateMonthlyCloseChecklist(token, period, itemKey, done, businessId);
    load();
  };

  const doClose = async () => {
    setMsg("");
    try {
      await closeMonth(token, period, businessId, closeNotes.trim() || undefined);
      setCloseNotes("");
      setMsg(`${period} books closed.`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not close this period yet.");
    }
  };

  const doReopen = async () => {
    if (!reopenReason.trim()) { setMsg("A reason is required to reopen a closed period."); return; }
    await reopenMonth(token, period, reopenReason, businessId);
    setReopenReason("");
    load();
  };

  if (!summary) return null;
  const isClosed = summary.close.status === "closed";

  return (
    <div id="month-end-close-card" className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm mb-6" data-testid="month-end-close-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h3 className="font-bold font-display text-[#0F172A]">{period} Month-End Close</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setPeriod((p) => { const [y, m] = p.split("-").map(Number); const d = new Date(y, m - 2, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; })}
            className="rounded-lg border border-[#E2E8F0] px-2 py-1 text-sm">◀</button>
          <button onClick={() => setPeriod((p) => { const [y, m] = p.split("-").map(Number); const d = new Date(y, m, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; })}
            className="rounded-lg border border-[#E2E8F0] px-2 py-1 text-sm">▶</button>
        </div>
      </div>
      <p className="text-xs text-[#94A3B8] mb-1">
        {isClosed ? `Closed${summary.close.closed_by ? ` by ${summary.close.closed_by}` : ""}${summary.close.closed_at ? ` on ${summary.close.closed_at.slice(0, 10)}` : ""}.`
          : `This is ${businessName || "this business"}'s own internal bookkeeping target, not a BIR filing deadline — see each step for what it actually means.`}
      </p>
      <p className="text-sm font-semibold text-[#0F172A] mb-4">{summary.steps_completed} of {summary.steps_total} completed</p>

      <div className="grid gap-2 sm:grid-cols-2 mb-4 text-xs">
        <p className="rounded-lg bg-[#F8FAFC] px-3 py-2">Sales: <strong>{summary.totals.sales_count}</strong> ({money(summary.totals.sales_gross_minor)})</p>
        <p className="rounded-lg bg-[#F8FAFC] px-3 py-2">Payments: <strong>{summary.totals.payments_count}</strong> ({money(summary.totals.payments_gross_minor)})</p>
        <p className="rounded-lg bg-[#F8FAFC] px-3 py-2">Fees: {money(summary.totals.fees_minor)}</p>
        <p className="rounded-lg bg-[#F8FAFC] px-3 py-2">Refunds: {summary.totals.refunds_count} ({money(summary.totals.refunds_minor)})</p>
        <p className="rounded-lg bg-[#F8FAFC] px-3 py-2">Expenses: {summary.totals.expenses_count} ({money(summary.totals.expenses_minor)})</p>
        <p className={`rounded-lg px-3 py-2 ${summary.totals.unreconciled_count > 0 ? "bg-red-50 text-red-700 font-semibold" : "bg-[#F8FAFC]"}`}>Unreconciled: {summary.totals.unreconciled_count}</p>
      </div>

      <div className="space-y-2 mb-4">
        {summary.groups.map((g) => (
          <div key={g.key} className="rounded-lg border border-[#E2E8F0]">
            <div className="flex items-center justify-between gap-3 px-4 py-2.5">
              <button type="button" onClick={() => setExpanded((k) => (k === g.key ? null : g.key))} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                <span className={`h-4 w-4 shrink-0 rounded-full ${g.done ? "bg-[#10B981]" : "bg-slate-300"}`} />
                <span className="text-sm font-medium text-[#0F172A]">{g.label}</span>
              </button>
              <button type="button" onClick={() => onGoTo(g.where.tab, g.where.focus)} className="shrink-0 text-xs font-semibold text-[#0F172A] underline">
                {g.where.label} →
              </button>
            </div>
            {expanded === g.key && g.items.length > 0 && (
              <div className="border-t border-[#E2E8F0] px-4 py-2 space-y-1.5">
                {g.items.map((it) => (
                  <label key={it.key} className="flex items-center gap-2 text-xs text-[#475569]">
                    <input type="checkbox" aria-label={it.key.replace(/_/g, " ")} checked={it.done} disabled={isClosed || !can("close_month_work")} onChange={(e) => toggleItem(it.key, e.target.checked)} />
                    {it.key.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
            )}
            {expanded === g.key && g.items.length === 0 && (
              <div className="border-t border-[#E2E8F0] px-4 py-2 text-xs text-[#475569]">
                Computed live from actual unreconciled payments — not a checkbox. {summary.totals.unreconciled_count === 0 ? "Nothing unresolved right now." : `${summary.totals.unreconciled_count} payment(s) still need review.`}
              </div>
            )}
          </div>
        ))}
      </div>

      {summary.block_reasons.length > 0 && !isClosed && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-semibold text-amber-800">Before this period can be closed:</p>
          <ul className="mt-1 list-disc pl-4 text-xs text-amber-800">
            {summary.block_reasons.map((r) => <li key={r}>{r}</li>)}
          </ul>
        </div>
      )}

      {msg && <p className="mb-3 text-xs text-[#10B981]">{msg}</p>}

      {!isClosed ? (
        <div className="flex flex-wrap items-center gap-2">
          <input aria-label="Closing note" value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} placeholder="Closing note (optional — kept permanently with this close's snapshot)"
            className="min-w-[260px] flex-1 rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs" />
          {can("close_month") ? (
            <button onClick={doClose} disabled={!summary.can_close}
              className="rounded-lg bg-[#0F172A] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40">
              Close {period} Books
            </button>
          ) : <p className="text-xs text-[#475569]" data-testid="close-restricted-note">Your Finance role can work the checklist but not close the books.</p>}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {can("reopen_month") ? (
            <>
              <input aria-label="Reason for reopening" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="Reason for reopening (required)"
                className="min-w-[220px] flex-1 rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs" />
              <button onClick={doReopen} className="rounded-lg border border-[#E2E8F0] px-4 py-2 text-sm font-medium text-[#0F172A] hover:border-[#0F172A]">Reopen period</button>
            </>
          ) : <p className="text-xs text-[#475569]" data-testid="reopen-restricted-note">Only the Business Owner can reopen a closed period.</p>}
        </div>
      )}
    </div>
  );
}

function OverviewTab({ token, onGoTo, businessId, businessName, includeTestPlans }) {
  const [data, setData] = useState(null);
  const isOperator = businessId === "mysitecare";
  useEffect(() => { fetchBillingOverview(token, includeTestPlans).then(setData).catch(() => {}); }, [token, includeTestPlans]);
  const currencies = data ? Object.keys(data.by_currency || {}) : [];
  return (
    <div>
      <ComplianceRemindersCard token={token} onGoTo={onGoTo} businessId={businessId} />
      <SetupChecklistCard token={token} onGoTo={onGoTo} businessId={businessId} />
      <MonthEndCloseCard token={token} onGoTo={onGoTo} businessId={businessId} businessName={businessName} />
      {!isOperator && (
        <p className="mb-6 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-xs text-sky-800" data-testid="non-operator-overview-note">
          Sales, payments, and reconciliation totals below are still MySiteCare's own — {businessName || "this business"}'s own separate sales/payments tracking isn't built yet. The reminders, setup checklist, and month-end close above are already scoped to {businessName || "this business"}.
        </p>
      )}
      {!data ? <p className="text-[#475569]">Loading…</p> : (
        <div data-testid="finance-overview-by-currency">
          {!data.transactional_posting && (
            <p className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-800" data-testid="non-transactional-posting-warning">
              This database does not support multi-document transactions. Financial postings are blocked until a replica set or mongos is configured; this prevents incomplete accounting writes.
            </p>
          )}
          {currencies.length === 0 && <p className="text-[#94A3B8] text-sm">No financial activity yet.</p>}
          {currencies.map((cur) => {
            const c = data.by_currency[cur];
            return (
              <div key={cur} className="mb-6" data-testid={`finance-overview-currency-${cur}`}>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#475569]">{cur}</p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <StatCard label="Recognized revenue" value={money(c.recognized_revenue_minor, cur)} />
                  <StatCard label="Gross billed" value={money(c.gross_billed_minor, cur)} />
                  <StatCard label="Gross collected" value={money(c.gross_collected_minor, cur)} />
                  <StatCard label="Outstanding receivables" value={money(c.outstanding_receivables_minor, cur)} tone="text-amber-600" />
                  <StatCard label="Unearned deposits (billed, not yet paid)" value={money(c.unearned_deposits_minor, cur)} />
                  <StatCard label="PayMongo fees" value={money(c.paymongo_fees_minor, cur)} />
                  <StatCard label="Net settlements" value={money(c.net_settlements_minor, cur)} />
                  <StatCard label="Refunds" value={money(c.refunds_minor, cur)} />
                </div>
              </div>
            );
          })}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Subscriptions active" value={data.subscriptions_active} />
            <StatCard label="Subscriptions past due" value={data.subscriptions_past_due} tone={data.subscriptions_past_due > 0 ? "text-amber-600" : undefined} />
            <StatCard label="Unreconciled payments" value={data.unreconciled_payments} tone={data.unreconciled_payments > 0 ? "text-red-600" : undefined} />
            <StatCard label="Failed payments" value={data.failed_payments} tone={data.failed_payments > 0 ? "text-red-600" : undefined} />
          </div>
        </div>
      )}
    </div>
  );
}

const ACCOUNT_TYPE_LABEL = { asset: "Assets", liability: "Liabilities", equity: "Equity", revenue: "Revenue", expense: "Expenses" };
const ACCOUNT_TYPE_ORDER = ["asset", "liability", "equity", "revenue", "expense"];

// Chart of Accounts (Finance Phase 2, 2026-09-22) — business-scoped: the selected
// business's own accounts, per-business-unique codes, honestly-labeled seed accounts,
// deactivate-over-delete once an account has posted history, system accounts
// protected from deletion.
function ChartOfAccountsTab({ token, businessId, businessName }) {
  const can = useFinanceCan();
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [newForm, setNewForm] = useState({ code: "", name: "", account_type: "asset", normal_balance: "debit", is_contra: false, description: "" });

  const load = () => { fetchAccounts(token, businessId, statusFilter === "all" ? undefined : statusFilter).then(setData).catch(() => {}); };
  useEffect(load, [token, businessId, statusFilter]);

  const submitNew = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createAccount(token, { ...newForm, business_id: businessId });
      setShowNew(false);
      setNewForm({ code: "", name: "", account_type: "asset", normal_balance: "debit", is_contra: false, description: "" });
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create this account.");
    }
  };

  const startEdit = (account) => { setEditingId(account.id); setEditForm({ name: account.name, description: account.description || "" }); };
  const saveEdit = async (accountId) => {
    await updateAccount(token, accountId, { ...editForm, business_id: businessId });
    setEditingId(null);
    load();
  };

  const doArchive = async (accountId) => { await archiveAccount(token, accountId, businessId); load(); };
  const doActivate = async (accountId) => { await activateAccount(token, accountId, businessId); load(); };
  const doDelete = async (accountId) => {
    setError("");
    try { await deleteAccount(token, accountId, businessId); load(); }
    catch (err) { setError(err?.response?.data?.detail || "Could not delete this account."); }
  };

  if (!data) return <p className="text-[#475569]">Loading…</p>;
  const grouped = ACCOUNT_TYPE_ORDER.map((t) => ({ type: t, accounts: data.accounts.filter((a) => a.account_type === t) })).filter((g) => g.accounts.length > 0);

  return (
    <div>
      <div className="mb-4 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2 text-xs text-[#475569]" data-testid="account-seed-note">{data.seeded_note}</div>
      {error && <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">{error}</p>}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label htmlFor="coa-status-filter" className="text-xs font-medium uppercase text-[#94A3B8]">Status</label>
          <select id="coa-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-[#E2E8F0] bg-white px-2 py-1.5 text-sm">
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="all">All</option>
          </select>
        </div>
        {can("manage_accounts") ? (
          <button onClick={() => setShowNew((v) => !v)} className="rounded-lg bg-[#0F172A] px-3 py-1.5 text-sm font-semibold text-white">
            {showNew ? "Cancel" : "+ New Account"}
          </button>
        ) : <span className="text-xs text-[#475569]" data-testid="accounts-readonly-note">Your Finance role can view the Chart of Accounts but not change it.</span>}
      </div>

      {showNew && (
        <form onSubmit={submitNew} className="mb-6 rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
          <h3 className="mb-3 font-bold font-display text-[#0F172A]">New account for {businessName || "this business"}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="new-account-code" className="text-xs font-medium uppercase text-[#475569]">Account code</label>
              <input id="new-account-code" required value={newForm.code} onChange={(e) => setNewForm({ ...newForm, code: e.target.value })}
                placeholder="e.g. petty_cash" className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
            </div>
            <div>
              <label htmlFor="new-account-name" className="text-xs font-medium uppercase text-[#475569]">Account name</label>
              <input id="new-account-name" required value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="e.g. Petty Cash" className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
            </div>
            <div>
              <label htmlFor="new-account-type" className="text-xs font-medium uppercase text-[#475569]">Type</label>
              <select id="new-account-type" value={newForm.account_type} onChange={(e) => setNewForm({ ...newForm, account_type: e.target.value })}
                className="mt-1 w-full rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm">
                {ACCOUNT_TYPE_ORDER.map((t) => <option key={t} value={t}>{ACCOUNT_TYPE_LABEL[t]}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="new-account-balance" className="text-xs font-medium uppercase text-[#475569]">Normal balance</label>
              <select id="new-account-balance" value={newForm.normal_balance} onChange={(e) => setNewForm({ ...newForm, normal_balance: e.target.value })}
                className="mt-1 w-full rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm">
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 text-xs font-medium text-[#475569]">
                <input type="checkbox" checked={newForm.is_contra} onChange={(e) => setNewForm({ ...newForm, is_contra: e.target.checked })} />
                Contra account (its normal balance runs opposite to its type — e.g. a contra-revenue account)
              </label>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="new-account-description" className="text-xs font-medium uppercase text-[#475569]">Description (optional)</label>
              <textarea id="new-account-description" value={newForm.description} onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" rows={2} />
            </div>
          </div>
          <button type="submit" className="mt-4 rounded-lg bg-[#10B981] px-4 py-2 text-sm font-semibold text-white">Create account</button>
        </form>
      )}

      {grouped.map((group) => (
        <div key={group.type} className="mb-6">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#475569]">{ACCOUNT_TYPE_LABEL[group.type]}</p>
          <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto">
            <table className="w-full min-w-[700px] text-left text-sm">
              <thead className="bg-[#F1F5F9] text-xs uppercase tracking-wide text-[#475569]">
                <tr>
                  <th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Normal balance</th>
                  <th className="px-4 py-3">Flags</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {group.accounts.map((a) => (
                  <tr key={a.id}>
                    <td className="px-4 py-3 font-mono text-xs text-[#475569]">{a.code}</td>
                    <td className="px-4 py-3">
                      {editingId === a.id ? (
                        <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="w-full rounded-lg border border-[#E2E8F0] px-2 py-1 text-sm" />
                      ) : (
                        <span className="font-medium text-[#0F172A]">{a.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 capitalize">{a.normal_balance}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {a.is_contra && <Badge className="border-violet-200 bg-violet-50 text-violet-700">Contra</Badge>}
                        {a.is_system && <Badge className="border-amber-200 bg-amber-50 text-amber-700">System</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusChip label={a.status === "active" ? "Complete" : "Internal recommendation"} /></td>
                    <td className="px-4 py-3">
                      {can("manage_accounts") && (
                      <div className="flex flex-wrap gap-2">
                        {editingId === a.id ? (
                          <>
                            <button onClick={() => saveEdit(a.id)} className="text-xs font-semibold text-[#10B981] hover:underline">Save</button>
                            <button onClick={() => setEditingId(null)} className="text-xs font-semibold text-[#475569] hover:underline">Cancel</button>
                          </>
                        ) : (
                          <button onClick={() => startEdit(a)} className="text-xs font-semibold text-[#0F172A] hover:underline">Edit</button>
                        )}
                        {a.status === "active"
                          ? <button onClick={() => doArchive(a.id)} className="text-xs font-semibold text-amber-700 hover:underline">Archive</button>
                          : <button onClick={() => doActivate(a.id)} className="text-xs font-semibold text-[#10B981] hover:underline">Activate</button>}
                        {!a.is_system && <button onClick={() => doDelete(a.id)} className="text-xs font-semibold text-red-600 hover:underline">Delete</button>}
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Finance Document Center (Phase 5, 2026-09-26)
// ---------------------------------------------------------------------------
const DOCUMENT_TYPE_OPTIONS = [
  "BIR Certificate of Registration (Form 2303)", "TIN card / ID", "Books of Accounts registration (Authority to Use / permit)",
  "ORUS registration / acknowledgment", "Filed return", "Filing confirmation (eBIRForms / eFPS)", "Tax payment proof",
  "Withholding tax certificate (Form 2307)", "Expense evidence", "Invoice / supporting document", "Bank statement",
  "BIR correspondence", "Accountant notes", "Other",
];

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

const LINK_PRESETS = [
  { id: "cor", label: "COR (Certificate of Registration)", kind: "profile_field", ref: "registration_date" },
  { id: "tin", label: "TIN verification", kind: "profile_field", ref: "tin" },
  { id: "rdo", label: "RDO verification", kind: "profile_field", ref: "rdo_number" },
  { id: "election", label: "8% election", kind: "profile_field", ref: "eight_percent_election" },
  { id: "vat", label: "VAT / non-VAT status", kind: "profile_field", ref: "vat_status" },
  { id: "books", label: "Books of Accounts registration", kind: "books_registration", ref: "books_of_accounts" },
  { id: "orus", label: "ORUS preparation", kind: "orus_preparation", ref: "orus" },
  { id: "field", label: "Another taxpayer-profile field…", kind: "profile_field", ref: null, hint: "Field key, e.g. registered_address" },
  { id: "filing", label: "Filing record", kind: "filing", ref: "@filing" },
  { id: "year", label: "Tax year", kind: "tax_year", ref: null, hint: "e.g. 2026" },
  { id: "period", label: "Tax period", kind: "tax_period", ref: null, hint: "e.g. 2026-Q1" },
  { id: "expense", label: "Expense", kind: "expense", ref: null, hint: "Expense id" },
  { id: "tx", label: "Recorded sale / receipt / fee", kind: "business_transaction", ref: null, hint: "Transaction id" },
  { id: "journal", label: "Supporting accounting entry", kind: "journal", ref: null, hint: "Journal id" },
];
const LINK_KIND_LABEL = { profile_field: "Profile field", books_registration: "Books registration", orus_preparation: "ORUS", filing: "Filing", tax_year: "Tax year", tax_period: "Tax period", expense: "Expense", business_transaction: "Transaction", journal: "Journal entry", invoice: "Invoice", payment: "Payment", filing_obligation: "Filing obligation" };

function DocumentLinkEditor({ token, businessId, doc, filings, onChanged }) {
  const can = useFinanceCan();
  const [preset, setPreset] = useState(LINK_PRESETS[0].id);
  const [ref, setRef] = useState("");
  const [err, setErr] = useState("");
  const chosen = LINK_PRESETS.find((x) => x.id === preset);
  const add = async () => {
    setErr("");
    const refId = chosen.ref === "@filing" ? ref : chosen.ref || ref;
    try { await linkComplianceDocument(token, businessId, doc.id, chosen.kind, refId); setRef(""); onChanged(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not link."); }
  };
  const remove = async (l) => {
    setErr("");
    try { await unlinkComplianceDocument(token, businessId, doc.id, l.kind, l.ref_id); onChanged(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not unlink."); }
  };
  return (
    <div className="text-xs">
      <ul className="flex flex-wrap gap-1">
        {(doc.links || []).map((l) => (
          <li key={`${l.kind}:${l.ref_id}`} className="inline-flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-0.5">
            {LINK_KIND_LABEL[l.kind] || l.kind}: {l.ref_id}
            {can("manage_documents") && <button type="button" onClick={() => remove(l)} aria-label={`Remove link ${l.kind} ${l.ref_id}`} className="text-[#94A3B8] hover:text-red-600">✕</button>}
          </li>
        ))}
        {(doc.links || []).length === 0 && <li className="text-[#94A3B8]">Not linked to anything yet</li>}
      </ul>
      {can("manage_documents") && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          <select aria-label={`What ${doc.filename} is evidence for`} value={preset} onChange={(e) => { setPreset(e.target.value); setRef(""); }} className="rounded border border-[#E2E8F0] bg-white px-1 py-1">
            {LINK_PRESETS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
          {chosen.ref === "@filing" && (
            <select aria-label="Filing record" value={ref} onChange={(e) => setRef(e.target.value)} className="rounded border border-[#E2E8F0] bg-white px-1 py-1">
              <option value="">Choose filing…</option>
              {filings.map((f) => <option key={f.id} value={f.id}>{f.tax_form} {f.tax_year} {f.tax_period}</option>)}
            </select>
          )}
          {chosen.ref === null && <input aria-label="Reference" value={ref} onChange={(e) => setRef(e.target.value)} placeholder={chosen.hint} className="w-36 rounded border border-[#E2E8F0] px-1 py-1" />}
          <button type="button" onClick={add} disabled={(chosen.ref === null || chosen.ref === "@filing") && !ref} className="rounded bg-[#0F172A] px-2 py-1 font-semibold text-white disabled:opacity-40">Link</button>
        </div>
      )}
      {err && <p role="alert" className="mt-1 text-red-700">{err}</p>}
    </div>
  );
}

function DocumentCenterTab({ token, businessId, businessName }) {
  const can = useFinanceCan();
  const [filings, setFilings] = useState([]);
  useEffect(() => { fetchTaxFilings(token, businessId).then(setFilings).catch(() => setFilings([])); }, [token, businessId]);
  const [docs, setDocs] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ documentType: DOCUMENT_TYPE_OPTIONS[0], taxYear: "", period: "", notes: "" });
  const [typeFilter, setTypeFilter] = useState("all");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ document_type: "", tax_year: "", period: "", notes: "" });

  const load = () => { fetchComplianceDocuments(token, businessId).then(setDocs).catch(() => setDocs([])); };
  useEffect(load, [token, businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) { setError("Choose a file to upload."); return; }
    setUploading(true);
    try {
      await uploadComplianceDocument(token, businessId, file, form);
      setShowUpload(false);
      setFile(null);
      setForm({ documentType: DOCUMENT_TYPE_OPTIONS[0], taxYear: "", period: "", notes: "" });
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not upload this document.");
    } finally {
      setUploading(false);
    }
  };
  const beginEdit = (doc) => { setEditing(doc); setEditForm({ document_type: doc.document_type, tax_year: doc.tax_year || "", period: doc.period || "", notes: doc.notes || "" }); };
  const saveEdit = async (e) => { e.preventDefault(); setError(""); try { await editComplianceDocument(token, businessId, editing.id, { ...editForm, tax_year: editForm.tax_year ? Number(editForm.tax_year) : null }); setEditing(null); load(); } catch (err) { setError(err?.response?.data?.detail || "Could not update document metadata."); } };
  const replace = async (doc, selected) => { if (!selected) return; setError(""); try { await replaceComplianceDocument(token, businessId, doc.id, selected); load(); } catch (err) { setError(err?.response?.data?.detail || "Could not replace the file."); } };
  const archive = async (doc) => { setError(""); try { await archiveComplianceDocument(token, businessId, doc.id); load(); } catch (err) { setError(err?.response?.data?.detail || "Could not archive document."); } };
  const remove = async (doc) => { if (!window.confirm(`Permanently delete ${doc.filename}? This cannot be undone.`)) return; setError(""); try { await deleteComplianceDocument(token, businessId, doc.id); load(); } catch (err) { const detail = err?.response?.data?.detail; setError(typeof detail === "object" ? `${detail.message} Linked to: ${(detail.links || []).map((l) => `${l.kind}: ${l.ref_id}`).join(", ")}` : detail || "Could not delete document."); } };

  if (!docs) return <p className="text-[#475569]">Loading…</p>;
  const documentTypes = Array.from(new Set(docs.map((d) => d.document_type))).sort();
  const visibleDocs = typeFilter === "all" ? docs : docs.filter((d) => d.document_type === typeFilter);

  return (
    <div>
      <p className="mb-4 text-xs text-[#475569]">Every BIR/compliance document for {businessName || "this business"}, stored once and linked as evidence to profile fields, Books of Accounts registration, ORUS, filings, tax years and periods, and accounting records. Open the evidence directly from those screens.</p>
      {error && <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">{error}</p>}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label htmlFor="doc-type-filter" className="text-xs font-medium uppercase text-[#94A3B8]">Type</label>
          <select id="doc-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded-lg border border-[#E2E8F0] bg-white px-2 py-1.5 text-sm">
            <option value="all">All types</option>
            {documentTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {can("manage_documents") ? <button onClick={() => setShowUpload((v) => !v)} className="rounded-lg bg-[#0F172A] px-3 py-1.5 text-sm font-semibold text-white">{showUpload ? "Cancel" : "+ Upload Document"}</button> : <span className="text-xs text-[#475569]" data-testid="documents-readonly-note">Your Finance role can view the register but not upload or link documents.</span>}
      </div>

      {showUpload && (
        <form onSubmit={submit} className="mb-6 rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
          <h3 className="mb-3 font-bold font-display text-[#0F172A]">Upload document for {businessName || "this business"}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="doc-file" className="text-xs font-medium uppercase text-[#475569]">File (PDF, image, spreadsheet, or CSV — up to 20MB)</label>
              <input id="doc-file" type="file" required onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
            </div>
            <div>
              <label htmlFor="doc-type" className="text-xs font-medium uppercase text-[#475569]">Document type</label>
              <select id="doc-type" value={form.documentType} onChange={(e) => setForm({ ...form, documentType: e.target.value })} className="mt-1 w-full rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm">
                {DOCUMENT_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="doc-year" className="text-xs font-medium uppercase text-[#475569]">Tax year (optional)</label>
              <input id="doc-year" type="number" value={form.taxYear} onChange={(e) => setForm({ ...form, taxYear: e.target.value })} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
            </div>
            <div>
              <label htmlFor="doc-period" className="text-xs font-medium uppercase text-[#475569]">Period (optional, e.g. Q1)</label>
              <input id="doc-period" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="doc-notes" className="text-xs font-medium uppercase text-[#475569]">Notes (optional)</label>
              <textarea id="doc-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
            </div>
          </div>
          <button type="submit" disabled={uploading} className="mt-4 rounded-lg bg-[#10B981] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{uploading ? "Uploading…" : "Upload"}</button>
        </form>
      )}
      {editing && <form onSubmit={saveEdit} className="mb-5 rounded-xl border border-[#10B981] bg-emerald-50 p-4" data-testid="document-edit-form"><div className="flex items-center justify-between gap-3"><h3 className="font-semibold">Edit metadata — {editing.filename}</h3><button data-testid="cancel-document-edit-button" type="button" onClick={() => setEditing(null)} className="text-xs underline">Cancel</button></div><div className="mt-3 grid gap-3 sm:grid-cols-2"><select data-testid="document-edit-type" value={editForm.document_type} onChange={(e) => setEditForm({ ...editForm, document_type: e.target.value })} className="rounded border p-2 text-sm">{DOCUMENT_TYPE_OPTIONS.map((t) => <option key={t}>{t}</option>)}</select><input data-testid="document-edit-year" type="number" value={editForm.tax_year} onChange={(e) => setEditForm({ ...editForm, tax_year: e.target.value })} placeholder="Tax year" className="rounded border p-2 text-sm" /><input data-testid="document-edit-period" value={editForm.period} onChange={(e) => setEditForm({ ...editForm, period: e.target.value })} placeholder="Period" className="rounded border p-2 text-sm" /><input data-testid="document-edit-notes" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Notes" className="rounded border p-2 text-sm" /></div><button data-testid="save-document-edit-button" className="mt-3 rounded bg-[#10B981] px-3 py-2 text-xs font-semibold text-white">Save metadata</button></form>}

      <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead className="bg-[#F1F5F9] text-xs uppercase tracking-wide text-[#475569]">
            <tr><th className="px-4 py-3">Filename</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Year / Period</th><th className="px-4 py-3">Uploaded</th><th className="px-4 py-3">Size</th><th className="px-4 py-3">Evidence for</th><th className="px-4 py-3">Action</th></tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {visibleDocs.map((d) => (
              <tr key={d.id}>
                <td className="px-4 py-3 font-medium text-[#0F172A]">{d.filename}</td>
                <td className="px-4 py-3">{d.document_type}</td>
                <td className="px-4 py-3 text-[#475569]">{[d.tax_year, d.period].filter(Boolean).join(" ") || "—"}</td>
                <td className="px-4 py-3 text-xs text-[#475569]">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : "—"} by {d.uploaded_by}</td>
                <td className="px-4 py-3 text-xs text-[#475569]">{formatBytes(d.size_bytes)}</td>
                <td className="px-4 py-3"><DocumentLinkEditor token={token} businessId={businessId} doc={d} filings={filings} onChanged={load} /></td>
                <td className="px-4 py-3"><div className="flex flex-wrap gap-2">{can("download_documents") && <><button data-testid={`view-document-${d.id}`} onClick={() => viewComplianceDocument(token, businessId, d.id)} className="text-xs font-semibold text-[#10B981] hover:underline">View</button><button data-testid={`download-document-${d.id}`} onClick={() => downloadComplianceDocument(token, businessId, d.id, d.filename)} className="text-xs font-semibold text-[#10B981] hover:underline">Download</button></>}{can("manage_documents") && <><button data-testid={`edit-document-${d.id}`} onClick={() => beginEdit(d)} className="text-xs font-semibold text-[#0F172A] hover:underline">Edit</button><details><summary data-testid={`more-document-${d.id}`} className="cursor-pointer text-xs font-semibold">⋯ More</summary><div className="mt-2 flex flex-col gap-2 rounded border bg-white p-2"><label className="cursor-pointer text-xs font-semibold text-[#0F172A]">Replace file<input data-testid={`replace-document-${d.id}`} type="file" className="hidden" onChange={(e) => replace(d, e.target.files?.[0])} /></label><button data-testid={`archive-document-${d.id}`} type="button" onClick={() => archive(d)} className="text-left text-xs font-semibold text-amber-700">Archive / Remove</button>{(d.links || []).length > 0 ? <p data-testid={`linked-document-warning-${d.id}`} className="text-xs text-[#475569]">Linked evidence: {(d.links || []).map((l) => `${l.kind}: ${l.ref_id}`).join(", ")}. Archive instead of delete.</p> : <button data-testid={`delete-document-${d.id}`} type="button" onClick={() => remove(d)} className="text-left text-xs font-semibold text-red-700">Permanently delete</button>}</div></details></>}</div></td>
              </tr>
            ))}
            {visibleDocs.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8]">No documents uploaded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Books & Reports (Finance Phase 4, 2026-09-23)
// ---------------------------------------------------------------------------
const BOOKS_SECTIONS = ["General Journal", "General Ledger", "Trial Balance", "Cash Receipts", "Cash Disbursements", "Sales Book", "Expense Book", "Transactions", "Books & Compliance", "Reports", "Packages"];
const DRILLABLE_SOURCE_TYPES = new Set(["invoice", "invoice_recognition", "payment", "fee", "expense", "refund"]);

function ExportBar({ onExport }) {
  const can = useFinanceCan();
  if (!can("export_reports")) return <p className="text-xs text-[#94A3B8]" data-testid="export-restricted-note">Downloads are not available to your Finance role.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {["csv", "xlsx", "pdf"].map((fmt) => (
        <button key={fmt} onClick={() => onExport(fmt)}
          className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-xs font-semibold text-[#475569] hover:border-[#0F172A] hover:text-[#0F172A]">
          Export {fmt.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function PeriodFilter({ period, setPeriod }) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor="books-period" className="text-xs font-medium uppercase text-[#94A3B8]">Period</label>
      <input id="books-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
        className="rounded-lg border border-[#E2E8F0] bg-white px-2 py-1.5 text-sm" />
      {period && <button onClick={() => setPeriod("")} className="text-xs font-medium text-[#475569] hover:underline">All periods</button>}
    </div>
  );
}

function SourceDrillDownModal({ token, source, onClose }) {
  const dialogRef = useDialogFocus(onClose);
  const [state, setState] = useState({ loading: true, doc: null, error: null });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, doc: null, error: null });
    fetchLedgerSource(token, source.source_type, source.source_id)
      .then((r) => { if (!cancelled) setState({ loading: false, doc: r.document, error: null }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, doc: null, error: err?.response?.data?.detail || "No source document available." }); });
    return () => { cancelled = true; };
  }, [token, source]);
  return (
    <div ref={dialogRef} className="fixed inset-0 z-50 flex justify-end bg-black/30" role="dialog" aria-modal="true" aria-label="Source document" onClick={onClose}>
      <div className="h-full w-full max-w-lg overflow-y-auto bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between border-b border-[#E2E8F0] bg-white p-5">
          <h2 className="text-lg font-bold font-display text-[#0F172A] capitalize">{source.source_type.replace(/_/g, " ")} — {source.source_id}</h2>
          <button onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#0F172A]">✕</button>
        </div>
        <div className="p-5">
          {state.loading && <p className="text-sm text-[#475569]">Loading…</p>}
          {state.error && <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{state.error}</p>}
          {state.doc && <pre className="whitespace-pre-wrap break-words rounded-lg bg-[#F8FAFC] p-4 text-xs text-[#0F172A]">{JSON.stringify(state.doc, null, 2)}</pre>}
        </div>
      </div>
    </div>
  );
}

const fmtAmt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(v ?? "");
};

// Account + quarter (+ currency) filters shared by the journal and ledger screens. Every one of
// them is applied by the BACKEND — the screen and the export always agree.
function BookFilters({ token, businessId, filters, setFilters, withCurrency = false }) {
  const [accounts, setAccounts] = useState([]);
  useEffect(() => { fetchAccounts(token, businessId).then((d) => setAccounts(d.accounts || [])).catch(() => setAccounts([])); }, [token, businessId]);
  const cls = "mt-1 block w-full min-w-0 rounded-lg border border-[#E2E8F0] bg-white px-2 py-1.5 text-sm sm:ml-2 sm:mt-0 sm:inline-block sm:w-auto sm:max-w-[16rem]";
  return (
    <div className="flex flex-wrap items-end gap-3" data-testid="book-filters">
      <label className="block w-full text-xs font-medium uppercase text-[#94A3B8] sm:w-auto" htmlFor="bk-account">Account
        <select id="bk-account" value={filters.account} onChange={(e) => setFilters({ ...filters, account: e.target.value })} className={cls}>
          <option value="">All accounts</option>{accounts.map((a) => <option key={a.id} value={a.code}>{`${a.code} — ${a.name}`}</option>)}
        </select>
      </label>
      <label className="block w-full text-xs font-medium uppercase text-[#94A3B8] sm:w-auto" htmlFor="bk-quarter">Quarter
        <select id="bk-quarter" value={filters.quarterNo} onChange={(e) => setFilters({ ...filters, quarterNo: e.target.value })} className={cls}>
          <option value="">Any</option>{[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
        </select>
      </label>
      {filters.quarterNo && (
        <label className="block w-full text-xs font-medium uppercase text-[#94A3B8] sm:w-auto" htmlFor="bk-year">Year
          <input id="bk-year" type="number" value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })} className={`${cls} sm:w-24`} />
        </label>
      )}
      {withCurrency && (
        <label className="block w-full text-xs font-medium uppercase text-[#94A3B8] sm:w-auto" htmlFor="bk-currency">Currency
          <input id="bk-currency" maxLength={3} value={filters.currency} onChange={(e) => setFilters({ ...filters, currency: e.target.value.toUpperCase() })} className={`${cls} sm:w-16`} />
        </label>
      )}
    </div>
  );
}

const EMPTY_BOOK_FILTERS = { account: "", quarterNo: "", year: String(new Date().getFullYear()), currency: "" };
const bookFilterParams = (f) => ({ account: f.account || undefined, quarter: f.quarterNo ? `${f.year}-Q${f.quarterNo}` : undefined, currency: f.currency || undefined });

function GeneralJournalSection({ token, businessId, businessName, period, onDrillDown, setMsg }) {
  const can = useFinanceCan();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState(EMPTY_BOOK_FILTERS);
  const load = () => { fetchGeneralJournal(token, period || undefined, businessId, "json", bookFilterParams(filters)).then((r) => setData(r.entries)).catch(() => setData([])); };
  useEffect(load, [token, businessId, period, filters]); // eslint-disable-line react-hooks/exhaustive-deps
  const doExport = (fmt) => fetchGeneralJournal(token, period || undefined, businessId, fmt, bookFilterParams(filters)).then(downloadReportPayload);

  const doReverse = async (journalId) => {
    const reason = window.prompt("Reason for reversing this journal entry:");
    if (!reason || !reason.trim()) return;
    try {
      await reverseJournal(token, journalId, reason, businessId);
      setMsg(`Journal ${journalId} reversed.`);
      load();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Could not reverse this entry.");
    }
  };

  if (!data) return <p className="text-[#475569]">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <BookFilters token={token} businessId={businessId} filters={filters} setFilters={setFilters} withCurrency />
        <ExportBar onExport={doExport} />
      </div>
      {data.length === 0 && (
        <p className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center text-sm text-[#94A3B8]">
          No journal entries for {businessName || "this business"}{period ? ` in ${period}` : ""}.
        </p>
      )}
      {data.map((g) => (
        <div key={g.journal_id} className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2">
            <div className="text-xs text-[#475569]">
              <span className="font-mono">{g.entry_date || new Date(g.created_at).toLocaleDateString()}</span>
              {" · "}<span className="capitalize">{g.source_type.replace(/_/g, " ")}</span>
              {DRILLABLE_SOURCE_TYPES.has(g.source_type) && (
                <button onClick={() => onDrillDown({ source_type: g.source_type, source_id: g.source_id })}
                  className="ml-2 font-semibold text-[#10B981] hover:underline">View source</button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {g.reversed_by_journal_id ? <Badge className="border-amber-200 bg-amber-50 text-amber-700">Reversed</Badge>
                : g.source_type === "adjustment" ? <Badge className="border-violet-200 bg-violet-50 text-violet-700">Reversal entry</Badge>
                  : can("reverse_journal") ? <button onClick={() => doReverse(g.journal_id)} className="text-xs font-semibold text-red-600 hover:underline">Reverse (correct this entry)</button> : null}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px] text-left text-sm">
              <tbody className="divide-y divide-[#E2E8F0]">
                {g.legs.map((l, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 font-mono text-xs text-[#475569]">{l.account}</td>
                    <td className="px-4 py-2 text-[#475569]">{l.memo}</td>
                    <td className="px-4 py-2 text-right">{l.direction === "debit" ? money(l.amount_minor, g.currency) : ""}</td>
                    <td className="px-4 py-2 text-right">{l.direction === "credit" ? money(l.amount_minor, g.currency) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function GeneralLedgerSection({ token, businessId, period, onDrillDown }) {
  const [statements, setStatements] = useState(null);
  const [filters, setFilters] = useState(EMPTY_BOOK_FILTERS);
  useEffect(() => {
    setStatements(null);
    fetchLedgerStatement(token, businessId, { period: period || undefined, ...bookFilterParams(filters) }).then((r) => setStatements(r.statements)).catch(() => setStatements([]));
  }, [token, businessId, period, filters]);
  const doExport = (fmt) => fetchBillingLedger(token, period || undefined, businessId, fmt, bookFilterParams(filters)).then(downloadReportPayload);

  if (!statements) return <p className="text-[#475569]">Loading…</p>;
  // Every number below — opening balance, running balance, totals, closing balance — is computed
  // by the BACKEND (_ledger_statements) and shared with the export. The browser only arranges them.
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <BookFilters token={token} businessId={businessId} filters={filters} setFilters={setFilters} withCurrency />
        <ExportBar onExport={doExport} />
      </div>
      {statements.length === 0 && <p className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center text-sm text-[#94A3B8]">No ledger activity{period ? ` in ${period}` : ""}.</p>}
      {statements.map((st) => (
        <div key={`${st.account}-${st.currency}`} className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid={`ledger-${st.account}-${st.currency}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2">
            <p className="font-mono text-sm font-semibold text-[#0F172A]">{st.account} <span className="font-sans text-xs font-normal text-[#94A3B8]">· {st.currency}</span></p>
            <p className="text-xs text-[#475569]">Opening <strong data-testid="ledger-opening">{money(st.opening_balance_minor, st.currency)}</strong> → Closing <strong data-testid="ledger-closing">{money(st.closing_balance_minor, st.currency)}</strong></p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <caption className="sr-only">{st.account} ledger in {st.currency}</caption>
              <thead className="bg-[#F1F5F9] text-xs uppercase tracking-wide text-[#475569]">
                <tr><th scope="col" className="px-4 py-2">Date</th><th scope="col" className="px-4 py-2">Source</th><th scope="col" className="px-4 py-2">Memo</th><th scope="col" className="px-4 py-2 text-right">Debit</th><th scope="col" className="px-4 py-2 text-right">Credit</th><th scope="col" className="px-4 py-2 text-right">Balance</th></tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                <tr className="bg-[#F8FAFC] text-xs font-semibold text-[#475569]"><td className="px-4 py-2" colSpan={5}>Opening balance</td><td className="px-4 py-2 text-right">{money(st.opening_balance_minor, st.currency)}</td></tr>
                {st.entries.map((e, i) => (
                  <tr key={e.id || i}>
                    <td className="px-4 py-2 text-xs text-[#475569]">{e.entry_date}</td>
                    <td className="px-4 py-2 text-xs capitalize text-[#475569]">{e.source_type.replace(/_/g, " ")}
                      {DRILLABLE_SOURCE_TYPES.has(e.source_type) && onDrillDown && <button onClick={() => onDrillDown({ source_type: e.source_type, source_id: e.source_id })} className="ml-2 font-semibold text-[#10B981] hover:underline">View</button>}
                    </td>
                    <td className="px-4 py-2 text-[#475569]">{e.memo}</td>
                    <td className="px-4 py-2 text-right">{e.direction === "debit" ? money(e.amount_minor, e.currency) : ""}</td>
                    <td className="px-4 py-2 text-right">{e.direction === "credit" ? money(e.amount_minor, e.currency) : ""}</td>
                    <td className="px-4 py-2 text-right font-semibold">{money(e.running_balance_minor, e.currency)}</td>
                  </tr>
                ))}
                <tr className="bg-[#F8FAFC] text-xs font-semibold text-[#0F172A]"><td className="px-4 py-2" colSpan={3}>Totals for the period</td><td className="px-4 py-2 text-right">{money(st.total_debits_minor, st.currency)}</td><td className="px-4 py-2 text-right">{money(st.total_credits_minor, st.currency)}</td><td className="px-4 py-2 text-right">{money(st.closing_balance_minor, st.currency)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function TrialBalanceSection({ token, businessId, period }) {
  const [data, setData] = useState(null);
  useEffect(() => { fetchBillingTrialBalance(token, period || undefined, businessId).then(setData).catch(() => setData({ by_currency: {} })); }, [token, businessId, period]);
  const doExport = (fmt) => fetchBillingTrialBalance(token, period || undefined, businessId, fmt).then(downloadReportPayload);

  if (!data) return <p className="text-[#475569]">Loading…</p>;
  const currencies = Object.keys(data.by_currency);
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><ExportBar onExport={doExport} /></div>
      {currencies.length === 0 && <p className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center text-sm text-[#94A3B8]">No postings{period ? ` in ${period}` : ""}.</p>}
      {currencies.map((cur) => {
        const d = data.by_currency[cur];
        const rows = Object.entries(d.balances_minor).filter(([, bal]) => bal !== 0);
        return (
          <div key={cur} className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto">
            <div className="flex items-center justify-between border-b border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2">
              <p className="font-semibold text-[#0F172A]">{cur}</p>
              <StatusChip label={d.balanced ? "Complete" : "Action required"} />
            </div>
            <table className="w-full min-w-[500px] text-left text-sm">
              <thead className="bg-[#F1F5F9] text-xs uppercase tracking-wide text-[#475569]"><tr><th className="px-4 py-2">Account</th><th className="px-4 py-2 text-right">Balance</th></tr></thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {rows.map(([account, bal]) => (
                  <tr key={account}><td className="px-4 py-2 font-mono text-xs text-[#475569]">{account}</td><td className="px-4 py-2 text-right">{money(bal, cur)}</td></tr>
                ))}
                <tr className="font-semibold"><td className="px-4 py-2">Total debits</td><td className="px-4 py-2 text-right">{money(d.total_debits_minor, cur)}</td></tr>
                <tr className="font-semibold"><td className="px-4 py-2">Total credits</td><td className="px-4 py-2 text-right">{money(d.total_credits_minor, cur)}</td></tr>
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function CashBookSection({ token, businessId, period, kind, onDrillDown }) {
  const [entries, setEntries] = useState(null);
  const fetcher = kind === "receipts" ? fetchCashReceipts : fetchCashDisbursements;
  useEffect(() => { fetcher(token, period || undefined, businessId).then((r) => setEntries(r.entries)).catch(() => setEntries([])); }, [token, businessId, period, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const doExport = (fmt) => fetcher(token, period || undefined, businessId, fmt).then(downloadReportPayload);

  if (!entries) return <p className="text-[#475569]">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><ExportBar onExport={doExport} /></div>
      <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead className="bg-[#F1F5F9] text-xs uppercase tracking-wide text-[#475569]">
            <tr><th className="px-4 py-2">Date</th><th className="px-4 py-2">Account</th><th className="px-4 py-2">Source</th><th className="px-4 py-2">Memo</th><th className="px-4 py-2 text-right">Amount</th></tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {entries.map((e, i) => (
              <tr key={i}>
                <td className="px-4 py-2 text-xs text-[#475569]">{e.date}</td>
                <td className="px-4 py-2 font-mono text-xs">{e.account}</td>
                <td className="px-4 py-2 capitalize">
                  {e.source_type.replace(/_/g, " ")}
                  {DRILLABLE_SOURCE_TYPES.has(e.source_type) && (
                    <button onClick={() => onDrillDown({ source_type: e.source_type, source_id: e.source_id })} className="ml-2 text-xs font-semibold text-[#10B981] hover:underline">View</button>
                  )}
                </td>
                <td className="px-4 py-2 text-[#475569]">{e.memo}</td>
                <td className="px-4 py-2 text-right">{e.currency} {fmtAmt(e.amount)}</td>
              </tr>
            ))}
            {entries.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#94A3B8]">No {kind === "receipts" ? "cash receipts" : "cash disbursements"} yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SalesBookSection({ token, businessId, businessName }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { setRows(null); fetchBillingReport(token, "sales-register", { fmt: "json", business_id: businessId }).then(setRows).catch(() => setRows([])); }, [token, businessId]);
  const doExport = (fmt) => fetchBillingReport(token, "sales-register", { fmt, business_id: businessId }).then(downloadReportPayload);

  if (!rows) return <p className="text-[#475569]">Loading…</p>;
  return (
    <div className="space-y-4">
      <p className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-xs text-sky-800" data-testid="sales-book-scope-note">{businessId === "mysitecare" ? "This is MySiteCare's own Sales Book, built from its invoices." : `This is ${businessName}'s own Sales Book, built only from sales recorded for this business (Transactions tab) — never MySiteCare's invoices.`}</p>
      <div className="flex justify-end"><ExportBar onExport={doExport} /></div>
      <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead className="bg-[#F1F5F9] text-xs uppercase tracking-wide text-[#475569]">
            <tr><th className="px-4 py-2">Date</th><th className="px-4 py-2">Invoice / OR #</th><th className="px-4 py-2">Description</th><th className="px-4 py-2 text-right">Gross</th><th className="px-4 py-2 text-right">Refund</th><th className="px-4 py-2 text-right">Net</th><th className="px-4 py-2">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="px-4 py-2 text-xs text-[#475569]">{r.invoice_date}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.invoice_number}</td>
                <td className="px-4 py-2">{r.description}</td>
                <td className="px-4 py-2 text-right">{r.currency} {fmtAmt(r.gross_amount)}</td>
                <td className="px-4 py-2 text-right">{r.currency} {fmtAmt(r.refund)}</td>
                <td className="px-4 py-2 text-right">{r.currency} {fmtAmt(r.net_sale)}</td>
                <td className="px-4 py-2 capitalize">{r.status}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8]">No sales yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExpenseBookSection({ token, businessId, businessName }) {
  const can = useFinanceCan();
  const [expenses, setExpenses] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ category: "", description: "", amount_minor: "", currency: "PHP", expense_date: new Date().toISOString().slice(0, 10) });
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => { fetchBillingExpenses(token, businessId).then(setExpenses).catch(() => setExpenses([])); };
  useEffect(load, [token, businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createBillingExpense(token, { ...form, amount_minor: Math.round(Number(form.amount_minor) * 100), business_id: businessId });
      setShowNew(false);
      setForm({ category: "", description: "", amount_minor: "", currency: "PHP", expense_date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not record this expense.");
    }
  };
  const saveEdit = async () => {
    setError(""); setMsg("");
    try {
      await patchExpense(token, businessId, editing.id, { category: editing.category, description: editing.description });
      setEditing(null); setMsg("Expense details corrected. The amount and date cannot be edited — post an adjusting entry for that."); load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save this correction.");
    }
  };

  if (!expenses) return <p className="text-[#475569]">Loading…</p>;
  return (
    <div className="space-y-4">
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">{error}</p>}
      {msg && <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs text-emerald-800">{msg}</p>}
      <div className="flex justify-end">
        {can("record_transactions") && <button onClick={() => setShowNew((v) => !v)} className="rounded-lg bg-[#0F172A] px-3 py-1.5 text-sm font-semibold text-white">{showNew ? "Cancel" : "+ New Expense"}</button>}
      </div>
      {showNew && (
        <form onSubmit={submit} className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
          <h3 className="mb-3 font-bold font-display text-[#0F172A]">New expense for {businessName || "this business"}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label htmlFor="exp-category" className="text-xs font-medium uppercase text-[#475569]">Category</label>
              <input id="exp-category" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" /></div>
            <div><label htmlFor="exp-date" className="text-xs font-medium uppercase text-[#475569]">Date</label>
              <input id="exp-date" type="date" required value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" /></div>
            <div className="sm:col-span-2"><label htmlFor="exp-desc" className="text-xs font-medium uppercase text-[#475569]">Description</label>
              <input id="exp-desc" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" /></div>
            <div><label htmlFor="exp-amount" className="text-xs font-medium uppercase text-[#475569]">Amount</label>
              <input id="exp-amount" type="number" min="0.01" step="0.01" required value={form.amount_minor} onChange={(e) => setForm({ ...form, amount_minor: e.target.value })} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" /></div>
            <div><label htmlFor="exp-currency" className="text-xs font-medium uppercase text-[#475569]">Currency</label>
              <input id="exp-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" /></div>
          </div>
          <button type="submit" className="mt-4 rounded-lg bg-[#10B981] px-4 py-2 text-sm font-semibold text-white">Record expense</button>
        </form>
      )}
      <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead className="bg-[#F1F5F9] text-xs uppercase tracking-wide text-[#475569]"><tr><th className="px-4 py-2">Date</th><th className="px-4 py-2">Category</th><th className="px-4 py-2">Description</th><th className="px-4 py-2 text-right">Amount</th>{can("record_transactions") && <th className="px-4 py-2">Action</th>}</tr></thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {expenses.map((e) => (editing && editing.id === e.id ? (
              <tr key={e.id} className="bg-[#F8FAFC]">
                <td className="px-4 py-2 text-xs text-[#475569]">{e.expense_date}</td>
                <td className="px-2 py-2"><label className="sr-only" htmlFor={`exp-edit-cat-${e.id}`}>Category</label><input id={`exp-edit-cat-${e.id}`} value={editing.category} onChange={(ev) => setEditing({ ...editing, category: ev.target.value })} className="w-full rounded border border-[#E2E8F0] px-2 py-1 text-sm" /></td>
                <td className="px-2 py-2"><label className="sr-only" htmlFor={`exp-edit-desc-${e.id}`}>Description</label><input id={`exp-edit-desc-${e.id}`} value={editing.description} onChange={(ev) => setEditing({ ...editing, description: ev.target.value })} className="w-full rounded border border-[#E2E8F0] px-2 py-1 text-sm" /></td>
                <td className="px-4 py-2 text-right">{money(e.amount_minor, e.currency)}</td>
                <td className="px-4 py-2 whitespace-nowrap"><button onClick={saveEdit} className="mr-2 text-xs font-semibold text-[#10B981] hover:underline">Save</button><button onClick={() => setEditing(null)} className="text-xs font-semibold text-[#475569] hover:underline">Cancel</button></td>
              </tr>
            ) : (
              <tr key={e.id}><td className="px-4 py-2 text-xs text-[#475569]">{e.expense_date}</td><td className="px-4 py-2">{e.category}</td><td className="px-4 py-2 text-[#475569]">{e.description}</td><td className="px-4 py-2 text-right">{money(e.amount_minor, e.currency)}</td>
                {can("record_transactions") && <td className="px-4 py-2"><button onClick={() => setEditing({ id: e.id, category: e.category, description: e.description })} className="text-xs font-semibold text-[#0F172A] hover:underline">Edit details</button></td>}</tr>
            )))}
            {expenses.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#94A3B8]">No expenses recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdjustmentForm({ token, businessId, businessName, onPosted }) {
  const [accounts, setAccounts] = useState([]);
  const [legs, setLegs] = useState([{ account: "", direction: "debit", amount_minor: "" }, { account: "", direction: "credit", amount_minor: "" }]);
  const [memo, setMemo] = useState("");
  const [adjDate, setAdjDate] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => { if (open) fetchAccounts(token, businessId, "active").then((d) => setAccounts(d.accounts)).catch(() => {}); }, [token, businessId, open]);

  const updateLeg = (i, field, value) => setLegs((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const addLeg = () => setLegs((prev) => [...prev, { account: "", direction: "debit", amount_minor: "" }]);
  const removeLeg = (i) => setLegs((prev) => prev.filter((_, idx) => idx !== i));

  const totalDebits = legs.reduce((sum, l) => sum + (l.direction === "debit" ? Number(l.amount_minor) || 0 : 0), 0);
  const totalCredits = legs.reduce((sum, l) => sum + (l.direction === "credit" ? Number(l.amount_minor) || 0 : 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createAdjustment(token, {
        business_id: businessId, memo, date: adjDate || undefined,
        legs: legs.map((l) => ({ account: l.account, direction: l.direction, amount_minor: Math.round(Number(l.amount_minor) * 100) })),
      });
      setLegs([{ account: "", direction: "debit", amount_minor: "" }, { account: "", direction: "credit", amount_minor: "" }]);
      setMemo("");
      setAdjDate("");
      setOpen(false);
      onPosted();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not post this adjustment.");
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
      <button onClick={() => setOpen((v) => !v)} className="text-sm font-semibold text-[#0F172A]">{open ? "Cancel new adjusting entry" : "+ New Adjusting Entry"}</button>
      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <p className="text-xs text-[#475569]">A manual correction for {businessName || "this business"}&rsquo;s own books. Always a new, separate, reasoned entry — never an edit of prior history.</p>
          {legs.map((leg, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <select aria-label={`Account (leg ${i + 1})`} value={leg.account} onChange={(e) => updateLeg(i, "account", e.target.value)} required className="rounded-lg border border-[#E2E8F0] bg-white px-2 py-1.5 text-sm">
                <option value="">Account…</option>
                {accounts.map((a) => <option key={a.id} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
              <select aria-label={`Direction (leg ${i + 1})`} value={leg.direction} onChange={(e) => updateLeg(i, "direction", e.target.value)} className="rounded-lg border border-[#E2E8F0] bg-white px-2 py-1.5 text-sm">
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>
              <input aria-label={`Amount (leg ${i + 1})`} type="number" min="0.01" step="0.01" required value={leg.amount_minor} onChange={(e) => updateLeg(i, "amount_minor", e.target.value)}
                placeholder="Amount" className="w-28 rounded-lg border border-[#E2E8F0] px-2 py-1.5 text-sm" />
              {legs.length > 2 && <button type="button" onClick={() => removeLeg(i)} className="text-xs font-semibold text-red-600 hover:underline">Remove</button>}
            </div>
          ))}
          <button type="button" onClick={addLeg} className="text-xs font-semibold text-[#0F172A] hover:underline">+ Add another leg</button>
          <div className="text-xs text-[#475569]">
            Debits: {totalDebits.toFixed(2)} · Credits: {totalCredits.toFixed(2)}
            {totalDebits !== totalCredits && <span className="ml-2 font-semibold text-red-600">— must balance before posting</span>}
          </div>
          <div>
            <label htmlFor="adj-date" className="text-xs font-medium uppercase text-[#475569]">Adjustment date (optional — defaults to today; must be in an open month)</label>
            <input id="adj-date" type="date" value={adjDate} onChange={(e) => setAdjDate(e.target.value)} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm sm:w-56" />
          </div>
          <div>
            <label htmlFor="adj-memo" className="text-xs font-medium uppercase text-[#475569]">Reason (required)</label>
            <textarea id="adj-memo" required value={memo} onChange={(e) => setMemo(e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
          </div>
          {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
          <button type="submit" disabled={totalDebits !== totalCredits || totalDebits === 0} className="rounded-lg bg-[#10B981] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Post adjusting entry</button>
        </form>
      )}
    </div>
  );
}

function BooksAndReportsTab({ token, businessId, businessName, onOpenTab = () => {}, initialSection = "General Journal" }) {
  const can = useFinanceCan();
  const [section, setSection] = useState(BOOKS_SECTIONS.includes(initialSection) ? initialSection : "General Journal");
  const [period, setPeriod] = useState("");
  const [drillDown, setDrillDown] = useState(null);
  const [msg, setMsg] = useState("");
  const [journalRefresh, setJournalRefresh] = useState(0);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {BOOKS_SECTIONS.map((s) => (
            <button key={s} onClick={() => setSection(s)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${section === s ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#0F172A]"}`}>{s}</button>
          ))}
        </div>
        {["General Journal", "General Ledger", "Trial Balance", "Cash Receipts", "Cash Disbursements"].includes(section) && <PeriodFilter period={period} setPeriod={setPeriod} />}
      </div>
      <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900" data-testid="books-internal-copy-note">
        <strong>Internal / preparation copy.</strong> {businessName}'s books here are generated by MySiteCare from its recorded transactions. They are not BIR-registered books of accounts (manual, loose-leaf, CBA or CAS) unless that registration has been recorded and verified under BIR &amp; Tax → Books of Accounts Registration.
      </p>
      {msg && <p className="mb-4 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2 text-xs text-[#475569]">{msg}</p>}
      {section === "General Journal" && (
        <>
          <GeneralJournalSection key={journalRefresh} token={token} businessId={businessId} businessName={businessName} period={period} onDrillDown={setDrillDown} setMsg={setMsg} />
          {can("post_adjustments") && <AdjustmentForm token={token} businessId={businessId} businessName={businessName} onPosted={() => { setMsg("Adjustment posted."); setJournalRefresh((n) => n + 1); }} />}
        </>
      )}
      {section === "General Ledger" && <GeneralLedgerSection token={token} businessId={businessId} period={period} onDrillDown={setDrillDown} />}
      {section === "Trial Balance" && <TrialBalanceSection token={token} businessId={businessId} period={period} />}
      {section === "Cash Receipts" && <CashBookSection token={token} businessId={businessId} period={period} kind="receipts" onDrillDown={setDrillDown} />}
      {section === "Cash Disbursements" && <CashBookSection token={token} businessId={businessId} period={period} kind="disbursements" onDrillDown={setDrillDown} />}
      {section === "Sales Book" && <SalesBookSection token={token} businessId={businessId} businessName={businessName} />}
      {section === "Expense Book" && <ExpenseBookSection token={token} businessId={businessId} businessName={businessName} />}
      {section === "Transactions" && <TransactionsSection token={token} businessId={businessId} businessName={businessName} />}
      {section === "Books & Compliance" && <BooksComplianceWorkspace token={token} businessId={businessId} businessName={businessName} />}
      {section === "Packages" && <PackagesSection token={token} businessId={businessId} businessName={businessName} />}
      {section === "Reports" && <ReportsCatalogSection token={token} businessId={businessId} onOpenSection={setSection} onOpenTab={onOpenTab} />}
      {drillDown && <SourceDrillDownModal token={token} source={drillDown} onClose={() => setDrillDown(null)} />}
    </div>
  );
}

function CustomersTab({ token }) {
  const [customers, setCustomers] = useState(null);
  useEffect(() => { fetchBillingCustomers(token).then(setCustomers).catch(() => {}); }, [token]);
  if (!customers) return <p className="text-[#475569]">Loading…</p>;
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto">
      <table className="w-full min-w-[600px] text-left text-sm">
        <thead className="bg-[#F1F5F9] text-xs uppercase tracking-wide text-[#475569]">
          <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Currency</th><th className="px-4 py-3">Test</th></tr>
        </thead>
        <tbody className="divide-y divide-[#E2E8F0]">
          {customers.map((c) => (
            <tr key={c.id}><td className="px-4 py-3 font-medium text-[#0F172A]">{c.display_name}</td><td className="px-4 py-3 text-[#475569]">{c.email}</td><td className="px-4 py-3">{c.currency}</td><td className="px-4 py-3">{c.is_test ? "Yes" : ""}</td></tr>
          ))}
          {customers.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-[#94A3B8]">No customers yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PaymongoSampleEmailCard({ token }) {
  const [market, setMarket] = useState("ph");
  const [sample, setSample] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const request = async (action) => {
    setBusy(action); setError("");
    try { setSample(action === "send" ? await sendPaymongoSampleEmail(token, market) : await previewPaymongoSampleEmail(token, market)); }
    catch (err) { setError(err?.response?.data?.detail || "The sample email could not be rendered."); }
    finally { setBusy(""); }
  };
  return <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="paymongo-sample-email-card">
    <div className="flex flex-wrap items-start justify-between gap-3"><div><p className="text-xs font-semibold uppercase tracking-wide text-[#10B981]">Admin-only sample</p><h3 className="mt-1 font-bold">PayMongo customer confirmation email</h3><p className="mt-1 text-sm text-[#64748B]">Uses the real payment-confirmation layout with synthetic data only. No PayMongo call, order, invoice, payment, access, or accounting record is created.</p></div><p data-testid="paymongo-sample-recipient" className="rounded-lg bg-[#F8FAFC] px-3 py-2 text-sm">Recipient: <strong>info@mysitecare.eu</strong></p></div>
    <div className="mt-4 flex flex-wrap items-end gap-3"><label className="text-xs font-semibold text-[#475569]">Market<select data-testid="paymongo-sample-market" value={market} onChange={(event) => setMarket(event.target.value)} className="mt-1 block rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm"><option value="ph">Philippines</option><option value="eu">EU</option></select></label><button data-testid="preview-paymongo-sample-email-button" type="button" disabled={Boolean(busy)} onClick={() => request("preview")} className="rounded-lg border border-[#0F172A] px-4 py-2 text-sm font-semibold disabled:opacity-50">{busy === "preview" ? "Rendering…" : "Preview PayMongo Sample Email"}</button><button data-testid="send-paymongo-sample-email-button" type="button" disabled={Boolean(busy)} onClick={() => request("send")} className="rounded-lg bg-[#10B981] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy === "send" ? "Sending…" : "Send PayMongo Sample Email"}</button></div>
    {error && <p role="alert" data-testid="paymongo-sample-email-error" className="mt-3 text-sm text-red-700">{error}</p>}
    {sample && <div className="mt-5 border-t border-[#E2E8F0] pt-4" data-testid="paymongo-sample-email-result"><p data-testid="paymongo-sample-delivery-status" className="text-sm font-semibold text-[#0F172A]">{sample.status === "sent" ? "Test email sent" : sample.delivery_status === "preview_only" ? "Email rendered successfully" : sample.status === "skipped_no_provider_configured" ? "Email rendered successfully — email provider not configured" : `Delivery status: ${sample.status || "preview only"}`}</p><p className="mt-1 text-xs text-[#64748B]">Template: {sample.template_key} · {sample.fields?.payment_reference || "Synthetic sample"}</p>{sample.html_body && <iframe data-testid="paymongo-sample-email-preview" title="PayMongo sample email preview" sandbox="" srcDoc={sample.html_body} className="mt-4 h-[520px] w-full rounded-lg border border-[#E2E8F0] bg-white" />}</div>}
  </section>;
}

function PaymentsTab({ token, includeTestPlans }) {
  const [payments, setPayments] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const loadNotifications = () => fetchBillingNotifications(token).then(setNotifications).catch(() => setNotifications([]));
  useEffect(() => {
    fetchBillingPayments(token, includeTestPlans).then(setPayments).catch(() => {});
    loadNotifications();
    // loadNotifications is intentionally scoped to this token-bound tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, includeTestPlans]);
  const retry = async (paymentId) => {
    setRetrying(paymentId);
    try { await retryBillingNotification(token, paymentId); await loadNotifications(); }
    finally { setRetrying(null); }
  };
  if (!payments || notifications === null) return <p className="text-[#475569]">Loading…</p>;
  return (
    <div className="space-y-6">
    <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto">
      <table className="w-full min-w-[700px] text-left text-sm">
        <thead className="bg-[#F1F5F9] text-xs uppercase tracking-wide text-[#475569]">
          <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Provider</th><th className="px-4 py-3">Website / Invoice</th><th className="px-4 py-3">Gross</th><th className="px-4 py-3">Fee</th><th className="px-4 py-3">Net</th><th className="px-4 py-3">Status</th></tr>
        </thead>
        <tbody className="divide-y divide-[#E2E8F0]">
          {payments.map((p) => (
            <tr key={p.id}>
              <td className="px-4 py-3 text-[#475569]">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : new Date(p.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-3 capitalize">{p.provider}{p.is_test && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">Internal test plan</span>}</td>
              <td className="px-4 py-3 text-xs text-[#475569]">{p.website_slug}<br />{p.invoice_id || "—"}</td>
              <td className="px-4 py-3">{money(p.gross_amount_minor, p.currency)}</td>
              <td className="px-4 py-3">{money(p.processor_fee_minor, p.currency)}</td>
              <td className="px-4 py-3">{money(p.net_amount_minor, p.currency)}</td>
              <td className="px-4 py-3 capitalize">{p.status}</td>
            </tr>
          ))}
          {payments.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8]">No payments yet.</td></tr>}
        </tbody>
      </table>
      <div className="p-4 border-t border-[#E2E8F0]">
        <a onClick={async (e) => { e.preventDefault(); const r = await fetchBillingReport(token, "collection-register"); const blob = new Blob([r.content], { type: "text/csv" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = r.filename; a.click(); }}
          href="#" className="text-sm font-medium text-[#10B981] hover:underline cursor-pointer">Download Collection Register (CSV)</a>
      </div>
    </div>
    <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto" data-testid="billing-email-confirmations">
      <div className="border-b border-[#E2E8F0] p-4"><h3 className="font-bold">Payment email confirmations</h3><p className="text-xs text-[#64748B]">Postmark acceptance, preview, skipped, and failure states are shown truthfully.</p></div>
      <table className="w-full min-w-[850px] text-left text-sm">
        <thead className="bg-[#F1F5F9] text-xs uppercase tracking-wide text-[#475569]"><tr><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Website</th><th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Provider</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Message ID</th><th className="px-4 py-3">Action</th></tr></thead>
        <tbody className="divide-y divide-[#E2E8F0]">{notifications.map((n) => <tr key={n.idempotency_key}>
          <td className="px-4 py-3">{n.customer_email}</td><td className="px-4 py-3">{n.website_slug || "—"}</td><td className="px-4 py-3">{n.invoice_id}</td><td className="px-4 py-3 capitalize">{n.provider}</td><td className="px-4 py-3 capitalize">{n.status?.replaceAll("_", " ")}</td><td className="px-4 py-3 font-mono text-xs">{n.provider_message_id || "—"}</td><td className="px-4 py-3">{["failed", "skipped_no_provider_configured"].includes(n.status) ? <button disabled={retrying === n.payment_id} onClick={() => retry(n.payment_id)} className="font-semibold text-[#10B981] disabled:opacity-50">{retrying === n.payment_id ? "Retrying…" : "Retry"}</button> : "—"}</td>
        </tr>)}{notifications.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8]">No payment confirmation attempts yet.</td></tr>}</tbody>
      </table>
    </div>
    <PaymongoSampleEmailCard token={token} />
    </div>
  );
}

function ReconciliationTab({ token, includeTestPlans }) {
  const [data, setData] = useState(null);
  useEffect(() => { fetchBillingReconciliation(token, includeTestPlans).then(setData).catch(() => {}); }, [token, includeTestPlans]);
  if (!data) return <p className="text-[#475569]">Loading…</p>;
  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-3 mb-6">
        {Object.entries(data.counts_by_reconciliation_status).map(([status, count]) => (
          <StatCard key={status} label={status.replace(/_/g, " ")} value={count} tone={status === "unmatched" || status === "exception" ? "text-red-600" : undefined} />
        ))}
      </div>
      {data.exceptions.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
          <p className="font-semibold text-red-800 mb-2">Exceptions requiring review</p>
          <ul className="space-y-1 text-sm text-red-700">
            {data.exceptions.map((e) => <li key={e.payment_id}>{e.provider} — {money(e.gross_amount_minor)} — {e.status}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

const MONTH_NAMES_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CALENDAR_STATUS_STYLE = {
  overdue: "bg-red-50 text-red-700 border-red-200", due_today: "bg-amber-50 text-amber-800 border-amber-300",
  due_soon: "bg-amber-50 text-amber-700 border-amber-200", upcoming: "bg-white text-[#475569] border-[#E2E8F0]",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

// §60-62 — MySiteCare's own full-year tax calendar, reading directly from the SAME
// TaxFiling/TaxYearElection data the rest of this BIR & Tax tab already uses — never a
// second/disconnected tax system.
function AdminTaxCalendarCard({ token }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [calendar, setCalendar] = useState(null);
  useEffect(() => { fetchAdminTaxCalendar(token, year).then(setCalendar).catch(() => {}); }, [token, year]);
  if (!calendar) return null;

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm mb-6" data-testid="admin-tax-calendar">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold font-display text-[#0F172A]">MySiteCare Tax Calendar</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setYear((y) => y - 1)} className="rounded-lg border border-[#E2E8F0] px-2 py-1 text-sm">◀</button>
          <span className="text-sm font-medium">{year}</span>
          <button onClick={() => setYear((y) => y + 1)} className="rounded-lg border border-[#E2E8F0] px-2 py-1 text-sm">▶</button>
        </div>
      </div>
      {calendar.election_needs_verification && (
        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">Needs annual verification — no 8% election decision has been recorded for {year}.</p>
      )}
      <p className="text-xs font-semibold text-[#475569] mb-2">Next deadlines</p>
      {calendar.next_deadlines.length === 0 ? (
        <p className="text-sm text-[#94A3B8] mb-4">Nothing due.</p>
      ) : (
        <div className="space-y-2 mb-4">
          {calendar.next_deadlines.map((f) => (
            <div key={f.id} className={`rounded-lg border px-3 py-2 text-sm ${CALENDAR_STATUS_STYLE[f.display_status]}`}>
              {f.due_date} — {f.tax_form} {f.tax_period} <span className="ml-2 text-xs uppercase">{f.display_status.replace(/_/g, " ")}</span>
            </div>
          ))}
        </div>
      )}
      {calendar.monthly_close_open_periods.length > 0 && (
        <p className="mb-3 text-xs text-amber-700">Month-end not yet closed: {calendar.monthly_close_open_periods.join(", ")}.</p>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {MONTH_NAMES_SHORT.map((name, i) => {
          const items = calendar.months[String(i + 1)] || [];
          return (
            <div key={name} className="rounded-lg border border-[#E2E8F0] p-2">
              <p className="text-xs font-bold uppercase text-[#94A3B8]">{name}</p>
              {items.length === 0 ? <p className="text-xs text-[#CBD5E1]">—</p> : items.map((f) => (
                <p key={f.id} className={`mt-1 rounded px-1 text-xs ${CALENDAR_STATUS_STYLE[f.display_status]}`}>{f.due_date.slice(8)} {f.tax_form}</p>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BirTaxTab({ token }) {
  const year = new Date().getFullYear();
  const [profile, setProfile] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [elections, setElections] = useState([]);
  const [filings, setFilings] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [health, setHealth] = useState(null);
  const [tin, setTin] = useState("");
  const [saveMsg, setSaveMsg] = useState("");

  const load = () => {
    fetchTaxProfile(token).then(setProfile).catch(() => {});
    fetchTaxEstimate(token, year).then(setEstimate).catch(() => {});
    fetchTaxElections(token).then(setElections).catch(() => {});
    fetchTaxFilings(token).then(setFilings).catch(() => {});
    fetchIncomeClassifications(token).then(setClassifications).catch(() => {});
    fetchTaxAutomationHealth(token, year).then(setHealth).catch(() => {});
  };
  useEffect(load, [token, year]);

  const currentElection = elections.find((e) => e.tax_year === year);
  const currentClassification = classifications.find((c) => c.tax_year === year);

  const saveProfileField = async (patch) => {
    await patchTaxProfile(token, patch);
    setSaveMsg("Saved.");
    load();
    setTimeout(() => setSaveMsg(""), 2000);
  };

  const [electionNote, setElectionNote] = useState("");

  const setElectionStatus = async (status) => {
    await upsertTaxElection(token, { tax_year: year, eight_percent_status: status, notes: electionNote || undefined });
    setElectionNote("");
    load();
  };

  const [classificationNote, setClassificationNote] = useState("");
  const [vatSourceNote, setVatSourceNote] = useState("");

  const answerClassification = async (classification) => {
    await upsertIncomeClassification(token, {
      tax_year: year, classification,
      source: classificationNote ? classificationNote : "Owner confirmation",
      notes: classificationNote || undefined,
    });
    setClassificationNote("");
    load();
  };

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
        <h3 className="font-bold font-display text-[#0F172A] mb-1">Tax Profile — Admin Only</h3>
        <p className="text-xs text-[#94A3B8] mb-4">Nothing here is inferred — fill these in from your actual BIR registration documents.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="tax-profile-name" className="text-xs font-medium uppercase text-[#475569]">Registered taxpayer name</label>
            <input id="tax-profile-name" defaultValue={profile?.registered_taxpayer_name || ""} onBlur={(e) => saveProfileField({ registered_taxpayer_name: e.target.value })}
              className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
          </div>
          <div>
            <label htmlFor="tax-profile-rdo" className="text-xs font-medium uppercase text-[#475569]">RDO</label>
            <input id="tax-profile-rdo" defaultValue={profile?.rdo || ""} onBlur={(e) => saveProfileField({ rdo: e.target.value })}
              className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
          </div>
          <div>
            <label htmlFor="tax-profile-tin" className="text-xs font-medium uppercase text-[#475569]">TIN {profile?.tin_masked ? `(current: ${profile.tin_masked})` : "(not set)"}</label>
            <input id="tax-profile-tin" value={tin} onChange={(e) => setTin(e.target.value)} placeholder="Enter to update"
              className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
            <button onClick={() => tin && saveProfileField({ tin })} className="mt-2 text-xs font-medium text-[#10B981] hover:underline">Save TIN</button>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="tax-profile-vat" className="text-xs font-medium uppercase text-[#475569]">VAT status</label>
            <p className="mt-1 mb-1 text-xs text-[#94A3B8]">Record only what the COR's Tax Type section actually shows — never inferred from the 8% election alone.</p>
            <select id="tax-profile-vat" defaultValue={profile?.vat_status || "not_configured"}
              onBlur={(e) => saveProfileField({ vat_status: e.target.value, vat_status_source: vatSourceNote || undefined })}
              className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white">
              <option value="not_configured">Not configured</option>
              <option value="non_vat">Non-VAT</option>
              <option value="vat_registered">VAT-registered</option>
            </select>
            <label htmlFor="tax-profile-vat-source" className="mt-2 block text-xs font-medium uppercase text-[#475569]">Source (required to mark VAT status verified)</label>
            <input id="tax-profile-vat-source" value={vatSourceNote} onChange={(e) => setVatSourceNote(e.target.value)}
              placeholder='e.g. "BIR COR, Tax Type section — Non-VAT"'
              className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
            {profile?.vat_status_evidence ? (
              <p className="mt-1 text-xs text-emerald-700">VERIFIED — {profile.vat_status_evidence.source} (recorded by {profile.vat_status_evidence.confirmed_by})</p>
            ) : (
              <p className="mt-1 text-xs text-amber-700">Needs verification — select a status, enter its source above, then click away from the dropdown to save.</p>
            )}
          </div>
        </div>
        {saveMsg && <p className="mt-3 text-xs text-[#10B981]">{saveMsg}</p>}
      </div>

      {!profile?.profile_configured && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5" data-testid="tax-profile-not-configured-banner">
          <p className="font-bold text-amber-800">Tax profile not configured</p>
          <p className="mt-1 text-sm text-amber-800">No BIR registration information has been entered yet. This is a blank software state, not a fact about your actual tax registration — it does NOT mean any election was legally declined.</p>
        </div>
      )}

      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
        <h3 className="font-bold font-display text-[#0F172A] mb-1">Tax Year {year} — 8% Election</h3>
        <p className="text-xs text-[#94A3B8] mb-4">A tax election may be irrevocable for the current taxable year. Confirm with BIR/accountant before changing registration assumptions.</p>
        {!currentElection ? (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="election-needs-verification">
            <p className="text-sm font-semibold text-amber-800">8% election status: Needs verification</p>
            <p className="mt-1 text-xs text-amber-700">No decision has been recorded for {year} yet. This is NOT the same as "not selected" — it means nobody has confirmed the actual BIR status in this system. Verify against your registration documents, then record the real status below.</p>
          </div>
        ) : (
          <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="election-recorded">
            <p className="text-sm text-[#0F172A]">Current status: <strong className="capitalize">{currentElection.eight_percent_status.replace(/_/g, " ")}</strong>{currentElection.confirmed_by && <span className="text-xs text-[#94A3B8] font-normal"> — recorded by {currentElection.confirmed_by}</span>}</p>
            {currentElection.eight_percent_status === "confirmed_elected" && (
              <p className="mt-1 text-xs font-semibold text-emerald-800">VERIFIED for tax year {year}{currentElection.decision_date ? ` on ${currentElection.decision_date.slice(0, 10)}` : ""}.</p>
            )}
            {currentElection.notes && <p className="mt-1 text-xs text-emerald-700">Evidence/notes: {currentElection.notes}</p>}
            <p className="mt-2 text-xs text-amber-700">This applies to tax year {year} only — BIR requires the election to be signified for each taxable year, so {year + 1} will show as "Needs verification" again until separately confirmed.</p>
          </div>
        )}
        <label htmlFor="election-evidence-note" className="text-xs font-medium uppercase text-[#475569]">Evidence / source note (optional, saved with the next status you record below)</label>
        <textarea id="election-evidence-note" value={electionNote} onChange={(e) => setElectionNote(e.target.value)}
          placeholder='e.g. "BIR Certificate of Registration (Form 2303), 8% option marked YES"'
          className="mt-1 mb-3 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs" rows={2} />
        <div className="flex gap-2 flex-wrap">
          {["not_selected", "selected_pending_verification", "confirmed_elected", "ineligible"].map((s) => (
            <button key={s} onClick={() => setElectionStatus(s)}
              className="rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-xs font-medium text-[#0F172A] hover:border-[#10B981]">{s.replace(/_/g, " ")}</button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
        <h3 className="font-bold font-display text-[#0F172A] mb-1">Tax Year {year} — Income Classification</h3>
        <p className="text-xs text-[#94A3B8] mb-4">This changes the 8% formula: the ₱250,000 statutory reduction applies only to a purely self-employed taxpayer. A mixed-income earner's 8% option is computed on full gross sales, without that reduction, because it's already used once against compensation income. Never guessed — answered directly.</p>
        {!currentClassification ? (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="classification-needs-verification">
            <p className="text-sm font-semibold text-amber-800">Income classification: Needs verification</p>
            <p className="mt-1 text-xs text-amber-700">Does Jeanjen receive compensation/employment income in addition to MySiteCare business income during taxable year {year}?</p>
            <label htmlFor="classification-source-note" className="mt-2 block text-xs font-medium uppercase text-amber-700">Source / notes</label>
            <textarea id="classification-source-note" value={classificationNote} onChange={(e) => setClassificationNote(e.target.value)}
              placeholder='e.g. "Owner confirmation — no other employer in 2026" or "BIR COR"'
              className="mt-1 mb-2 w-full rounded-lg border border-amber-200 px-3 py-2 text-xs" rows={2} />
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => answerClassification("mixed_income")} className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:border-amber-500">Yes — mixed income</button>
              <button onClick={() => answerClassification("purely_self_employed")} className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:border-amber-500">No — purely self-employed</button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="classification-recorded">
            <p className="text-sm text-[#0F172A]">Current status: <strong className="capitalize">{currentClassification.classification.replace(/_/g, " ")}</strong> — recorded by {currentClassification.confirmed_by}</p>
            <p className="mt-1 text-xs font-semibold text-emerald-800">VERIFIED for tax year {year}. Source: {currentClassification.source}</p>
          </div>
        )}
      </div>

      {health && (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm" data-testid="tax-automation-health">
          <h3 className="font-bold font-display text-[#0F172A] mb-1">Tax Automation Health — {year}</h3>
          <p className={`mt-1 mb-3 text-sm font-bold ${health.overall === "READY FOR AUTOMATED TAX PREPARATION" ? "text-emerald-700" : "text-amber-700"}`}>{health.overall}</p>
          <div className="grid gap-2 sm:grid-cols-2 text-xs">
            <p>BIR profile: <strong>{health.bir_profile}</strong></p>
            <p>8% election: <strong>{health.election}</strong></p>
            <p>Income classification: <strong>{health.income_classification}</strong></p>
            <p>VAT status: <strong>{health.vat_status}</strong></p>
            <p>Tax calculation: <strong>{health.tax_calculation}</strong></p>
            <p>2026 obligations: <strong>{health.obligations}</strong></p>
            <p>Books: <strong>{health.books}</strong></p>
          </div>
          {health.blocking_reasons?.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800">Exact missing facts:</p>
              <ul className="mt-1 list-disc pl-4 text-xs text-amber-800">
                {health.blocking_reasons.map((r) => <li key={r}>{r}</li>)}
              </ul>
            </div>
          )}
          {health.first_obligation && (
            <div className="mt-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
              <p className="text-xs font-semibold text-[#0F172A]">First reportable filing period (best available — not BIR-confirmed):</p>
              {health.first_obligation.first_1701q ? (
                <p className="mt-1 text-xs text-[#475569]">1701Q {health.first_obligation.first_1701q.tax_period} {health.first_obligation.tax_year}, due {health.first_obligation.first_1701q.due_date}.</p>
              ) : (
                <p className="mt-1 text-xs text-[#475569]">{health.first_obligation.first_1701q_note}</p>
              )}
              <p className="mt-1 text-xs text-[#475569]">Annual return due {health.first_obligation.annual_return?.due_date}.</p>
              <p className="mt-2 text-xs italic text-[#94A3B8]">Recommended question for BIR/RDO/your accountant: "{health.first_obligation.recommended_question}"</p>
            </div>
          )}
        </div>
      )}

      <AdminTaxCalendarCard token={token} />

      {estimate && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2">{estimate.label} — planning only, not a filed figure</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard label="Gross sales YTD" value={money(estimate.estimate.gross_sales_minor)} />
            <StatCard label="Estimated 8% tax" value={money(estimate.estimate.estimated_tax_minor)} />
            <StatCard label="Estimated balance due" value={money(estimate.estimate.estimated_balance_minor)} />
          </div>
          <p className="mt-3 text-sm text-amber-800">{estimate.percentage_tax_reasoning}</p>
          {estimate.warning_band_bps && <p className="mt-2 text-sm font-semibold text-red-700">⚠ Gross sales are at {estimate.warning_band_bps / 100}% of the eligibility ceiling — review VAT/tax registration.</p>}
        </div>
      )}

      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold font-display text-[#0F172A]">Tax Filings</h3>
          <div className="flex gap-2">
            {["Q1", "Q2", "Q3"].map((q) => (
              <button key={q} onClick={() => createTaxFiling(token, { tax_form: "1701Q", tax_year: year, tax_period: q }).then(load)}
                className="rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-xs font-medium hover:border-[#10B981]">+ 1701Q {q}</button>
            ))}
            <button onClick={() => createTaxFiling(token, { tax_form: "1701A", tax_year: year, tax_period: "ANNUAL" }).then(load)}
              className="rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-xs font-medium hover:border-[#10B981]">+ 1701A Annual</button>
          </div>
        </div>
        <div className="space-y-2">
          {filings.filter((f) => f.tax_year === year).map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-lg border border-[#E2E8F0] px-4 py-3 text-sm">
              <span className="font-medium text-[#0F172A]">{f.tax_form} — {f.tax_period}</span>
              <span className="text-[#475569]">Due {f.due_date}</span>
              <span className="capitalize rounded-full bg-[#F1F5F9] px-2.5 py-0.5 text-xs font-medium">{f.status.replace(/_/g, " ")}</span>
              {f.status === "collecting_data" && <button onClick={() => changeTaxFilingStatus(token, f.id, { new_status: "needs_review" }).then(load)} className="text-xs text-[#10B981] hover:underline">Mark ready for review</button>}
              {f.status === "needs_review" && <button onClick={() => changeTaxFilingStatus(token, f.id, { new_status: "ready_to_file" }).then(load)} className="text-xs text-[#10B981] hover:underline">Mark ready to file</button>}
            </div>
          ))}
          {filings.filter((f) => f.tax_year === year).length === 0 && <p className="text-sm text-[#94A3B8]">No filings created for {year} yet.</p>}
        </div>
      </div>
    </div>
  );
}

function DiagnosticsTab({ token }) {
  const [data, setData] = useState(null);
  useEffect(() => { fetchBillingDiagnostics(token).then(setData).catch(() => {}); }, [token]);
  if (!data) return <p className="text-[#475569]">Loading…</p>;
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm space-y-3 text-sm">
      <p><strong>PayMongo configured:</strong> {data.paymongo.configured ? "Yes" : "No"}</p>
      {data.paymongo.configured && <p><strong>Live mode:</strong> {data.paymongo.livemode ? "Yes" : "No (test mode)"}</p>}
      <p><strong>Webhook secret configured:</strong> {data.paymongo.webhook_secret_configured ? "Yes" : "No"}</p>
      <p><strong>Subscription capability:</strong> {data.paymongo.subscription_capability ? "Enabled" : "Not enabled"}</p>
      {data.paymongo.note && <p className="text-amber-700 bg-amber-50 rounded-lg p-3">{data.paymongo.note}</p>}
      <p><strong>Last webhook received:</strong> {data.webhook_last_received_at || "Never"}</p>
      <p><strong>Unprocessed webhook events:</strong> {data.unprocessed_webhook_events}</p>
      <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3" data-testid="billing-integrity-diagnostics">
        <p className="font-semibold text-[#0F172A]">Billing integrity</p>
        <p>Paid payments: {data.billing_integrity?.paid_payments || 0} · Missing invoices: {data.billing_integrity?.paid_without_invoice || 0} · Missing active entitlements: {data.billing_integrity?.paid_without_active_entitlement || 0} · Missing accounting postings: {data.billing_integrity?.paid_without_accounting_posting || 0} · Duplicate provider IDs: {data.billing_integrity?.duplicate_provider_payment_ids || 0}</p>
      </div>
      <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3" data-testid="email-delivery-diagnostics">
        <p className="font-semibold text-[#0F172A]">Email delivery</p>
        <p>Missing attempts: {data.email_delivery?.paid_without_email_attempt || 0} · Submitted: {data.email_delivery?.submitted || 0} · Preview only: {data.email_delivery?.preview_only || 0} · Configuration missing: {data.email_delivery?.configuration_missing || 0} · Failed: {data.email_delivery?.failed || 0}</p>
        <p data-testid="email-delivery-confirmed">Confirmed delivered: {data.email_delivery?.delivered || 0} · Deferred: {data.email_delivery?.deferred || 0} · Bounced: {data.email_delivery?.bounced || 0} · Suppressed: {data.email_delivery?.suppressed || 0} · Spam complaints: {data.email_delivery?.spam_complaint || 0}</p>
      </div>
      {data.recent_webhook_errors.length > 0 && (
        <div className="rounded-lg bg-red-50 p-3">
          <p className="font-semibold text-red-800 mb-1">Recent webhook errors</p>
          {data.recent_webhook_errors.map((e) => <p key={e.provider_event_id} className="text-xs text-red-700">{e.event_type}: {e.error}</p>)}
        </div>
      )}
    </div>
  );
}

function AdSenseDiagnosticsCard() {
  // Sourced entirely from the frontend's own ads config — no backend call needed and
  // no secret is at stake (an AdSense client ID / ad-unit slot ID is meant to be
  // public, embedded in servable frontend code; it is not a credential). See
  // src/lib/adsConfig.js's own note on why the actual ID values are never echoed back
  // even here, despite not being secret — there's no operational reason to.
  const diag = adsenseDiagnostics();
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm space-y-2 text-sm mt-6" data-testid="adsense-diagnostics-card">
      <h3 className="font-bold font-display text-[#0F172A] mb-2">Google AdSense</h3>
      <p><strong>Enabled:</strong> {diag.enabled ? "Yes" : "No"}</p>
      <p><strong>Client ID configured:</strong> {diag.clientIdConfigured ? "Yes" : "Not configured"}</p>
      <p><strong>Consent integration:</strong> {diag.consentIntegration === "ready" ? "Ready" : "Needs setup"}</p>
      <p><strong>Placements with a slot configured:</strong> {diag.configuredPlacementCount} / {diag.totalKnownPlacements}</p>
      <p><strong>Sensitive routes blocked:</strong> Yes (admin, customer account portal, billing/checkout)</p>
      <p className="pt-1 text-xs text-[#94A3B8]">
        {diag.live
          ? "AdSense is configured and eligible public pages may show ads once a visitor accepts cookies."
          : "Google AdSense — Not configured. See reports/google-adsense-owner-runbook.md for setup steps."}
      </p>
    </div>
  );
}

// Business/tenant switcher (2026-09-22 Finance tenant-scoping pass) — Finance data
// now belongs to one business at a time: "mysitecare" (the platform operator's own
// books) or a client business (a billing_customers row, reusing Customer as the
// tenant anchor exactly as the pre-existing scoped tax routes already did). This
// reads the same GET /admin/billing/tax/businesses endpoint the scoped tax workspace
// itself already relies on, so the two always list the same businesses.
function BusinessSwitcher({ businesses, businessId, onChange }) {
  return (
    <div className="mb-6 flex items-center gap-2">
      <label htmlFor="finance-business-switcher" className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Books for</label>
      <select id="finance-business-switcher" value={businessId} onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm font-medium text-[#0F172A]">
        {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}{b.kind === "operator" ? " (operator)" : ""}</option>)}
      </select>
    </div>
  );
}

export default function AdminFinance() {
  const { token, checking, revealAdminUi, isLoggedIn } = useAdminAuth();
  const [tab, setTab] = useState("Overview");
  const [focusId, setFocusId] = useState(null);
  const [businessId, setBusinessId] = useState("mysitecare");
  const [includeTestPlans, setIncludeTestPlans] = useState(false);
  const [caps, setCaps] = useState(null);
  useEffect(() => { if (token) fetchFinanceCapabilities(token).then(setCaps).catch(() => setCaps({ role: "unknown", role_label: "Unknown role", capabilities: [] })); }, [token]);
  const can = (capability) => Boolean(caps && caps.capabilities.includes(capability));
  const [businesses, setBusinesses] = useState([{ id: "mysitecare", name: "MySiteCare", kind: "operator" }]);
  useEffect(() => { if (token) fetchTaxBusinesses(token).then(setBusinesses).catch(() => {}); }, [token]);
  const businessName = businesses.find((b) => b.id === businessId)?.name || "MySiteCare";

  // Every "Where do I do it?" deep link (detail drawer, month-end close groups) goes
  // through this one function — switch tab, then scroll/highlight the target element
  // once that tab's own data has had a moment to load (actionable-checklist task,
  // item 12: never just link to the top of the Finance page).
  const [booksSection, setBooksSection] = useState("General Journal");
  const goTo = (targetTab, focus) => {
    setTab(targetTab);
    // "section:Sales Book" opens that Books & Reports section (not an element to scroll to).
    if (focus && focus.startsWith("section:")) { setBooksSection(focus.slice("section:".length)); setFocusId(null); return; }
    setFocusId(focus || null);
  };

  useEffect(() => {
    if (!focusId) return undefined;
    const timer = setTimeout(() => {
      const el = document.getElementById(focusId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#10B981]", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#10B981]", "ring-offset-2"), 2500);
      }
      setFocusId(null);
    }, 350);
    return () => clearTimeout(timer);
  }, [tab, focusId]);

  if (checking) return null;
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;
  if (!isLoggedIn) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-slate-500">DEBUG — Admin UI revealed, but Finance data needs a real login.</div>;

  return (
    <FinanceCapsContext.Provider value={can}>
    <div className="min-h-screen bg-[#F8FAFC]">
      <Container className="py-10">
        <h1 className="text-2xl font-bold font-display text-[#0F172A] mb-1">Finance & BIR Tax</h1>
        <p className="text-sm text-[#475569] mb-6">Payments, accounting, reconciliation and Philippine tax compliance preparation.</p>
        <AdminNavTabs />
        <BusinessSwitcher businesses={businesses} businessId={businessId} onChange={setBusinessId} />
        <label className="mb-4 flex w-fit items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="finance-include-test-plans-control"><input type="checkbox" checked={includeTestPlans} onChange={(event) => setIncludeTestPlans(event.target.checked)} data-testid="finance-include-test-plans-checkbox" /> Include internal test plans (real live payments remain visible in the audit trail; review revenue and tax classification separately.)</label>
        {caps && <p className="mt-2 text-xs text-[#475569]" data-testid="finance-role-badge">Finance role: <strong>{caps.role_label}</strong></p>}
        <div className="flex gap-2 mt-6 mb-6 flex-wrap">
          {TABS.map((t) => (
            <button key={t} onClick={() => { setTab(t); setBooksSection("General Journal"); }}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#0F172A]"}`}>{t}</button>
          ))}
        </div>
        {tab === "Overview" && <OverviewTab token={token} onGoTo={goTo} businessId={businessId} businessName={businessName} includeTestPlans={includeTestPlans} />}
        {tab === "Chart of Accounts" && <ChartOfAccountsTab token={token} businessId={businessId} businessName={businessName} />}
        {tab === "Books & Reports" && <BooksAndReportsTab key={booksSection} token={token} businessId={businessId} businessName={businessName} onOpenTab={setTab} initialSection={booksSection} />}
        {tab === "Document Center" && <DocumentCenterTab token={token} businessId={businessId} businessName={businessName} />}
        {tab === "Customers" && <CustomersTab token={token} />}
        {tab === "Payments" && <PaymentsTab token={token} includeTestPlans={includeTestPlans} />}
        {tab === "Reconciliation" && <ReconciliationTab token={token} includeTestPlans={includeTestPlans} />}
        {tab === "BIR & Tax" && <ScopedTaxWorkspace token={token} businessId={businessId} businessName={businessName} can={can} />}
        {tab === "Diagnostics" && (
          <>
            <DiagnosticsTab token={token} />
            <AdSenseDiagnosticsCard />
          </>
        )}
      </Container>
    </div>
    </FinanceCapsContext.Provider>
  );
}
